import {
  Activity,
  AlertTriangle,
  Anchor,
  ArrowRightCircle,
  BriefcaseBusiness,
  CalendarDays,
  Check,
  CircleStop,
  ClipboardCopy,
  Clock,
  CornerDownRight,
  Download,
  FileText,
  LogIn,
  LogOut,
  MapPin,
  Merge as MergeIcon,
  Mic,
  Pencil,
  Pill,
  Play,
  Plus,
  RefreshCw,
  Save,
  ScanBarcode,
  Settings2,
  Square,
  Trash2,
  TrendingUp,
  Utensils,
  Wifi,
  WifiOff,
  X
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { IScannerControls } from "@zxing/browser";
import {
  active,
  buildInvoiceSummaries,
  clearClientLegacyRate,
  deleteClient,
  deleteClientRate,
  deleteFoodMeal,
  deleteFoodLog,
  deleteMealPreset,
  deleteMedicationLog,
  deleteProject,
  deleteSymptomLog,
  deleteVoiceNote,
  deleteWorkSession,
  effectiveClientRate,
  ensureClient,
  ensureProject,
  formatDateTime,
  formatDateInput,
  formatDuration,
  formatMoney,
  getActiveWorkSession,
  getTodayMedication,
  groupFoodLogsIntoMeals,
  inferMealType,
  initializeMealPresets,
  invoiceToCsv,
  loadSnapshot,
  localDateKey,
  logFood,
  logFoodGroup,
  logSymptom,
  matchLocationRule,
  medicationDoseCount,
  mergeClients,
  minutesBetween,
  parseClockInput,
  parseDateInput,
  planWeeklyCarryover,
  rankFoodItems,
  rankFrequentProjects,
  rankProjects,
  rankRecentProjects,
  renameClient,
  renameProject,
  repriceClientWorkSessions,
  searchFoodItems,
  saveClientRate,
  saveLocationRule,
  saveMealPreset,
  saveVoiceNote,
  setMedicationDose,
  startWorkSession,
  stopWorkSession,
  switchWorkSession,
  summarizeHealthDays,
  summarizeWorkDay,
  updateWorkSession,
  updateWorkSessionDuration,
  updateWorkSessionEndClock,
  updateWorkSessionStartClock,
  updateClientBillingIncrement,
  updateFoodMeal,
  updateFoodLog,
  updateMedicationLog,
  updateSymptomLog,
  updateVoiceNote,
  weekStartKey
} from "./domain";
import {
  lookupBarcode,
  normalizeDetectedBarcode,
  registerBarcodeCandidate,
  type BarcodeCandidate
} from "./barcode";
import { todayKey } from "./db";
import {
  MEDICATION_KINDS,
  MEDICATION_LABELS,
  SYMPTOM_KINDS,
  SYMPTOM_LABELS,
  SYMPTOM_ROW_SHORT_LABELS,
  SYMPTOM_TREND_KINDS,
  normalizeMedicationKind,
  symptomLogLabel
} from "./health";
import {
  getMe,
  getSyncDiagnostics,
  login,
  logout,
  registerBackgroundSync,
  SyncError,
  syncNow,
  type SyncDiagnostics
} from "./sync";
import {
  getPwaWorkerState,
  PWA_STATUS_EVENT,
  type PwaWorkerState
} from "./swRegistration";
import type { WeeklyCarryPlan } from "./domain";
import type {
  Client,
  ClientRate,
  FoodLog,
  FoodMeal,
  MealType,
  MealPreset,
  MedicationKind,
  MedicationLog,
  Project,
  SymptomKind,
  SymptomLog,
  TrackerSnapshot,
  VoiceNote,
  WorkSession
} from "./types";

type Tab = "work" | "wellbeing" | "trends" | "history" | "invoice" | "settings";
type WorkDay = NonNullable<ReturnType<typeof summarizeWorkDay>>;
type WorkWeek = { week: string; days: WorkDay[]; workedMinutes: number };

const mealTypeOptions: Array<{ value: MealType; label: string }> = [
  { value: "colazione", label: "Colazione" },
  { value: "pranzo", label: "Pranzo" },
  { value: "cena", label: "Cena" },
  { value: "spuntino", label: "Spuntino" }
];

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

interface NativeDetectedBarcode {
  rawValue: string;
  format: string;
}

interface NativeBarcodeDetector {
  detect(source: ImageBitmapSource): Promise<NativeDetectedBarcode[]>;
}

interface NativeBarcodeDetectorConstructor {
  new (options?: { formats?: string[] }): NativeBarcodeDetector;
  getSupportedFormats(): Promise<string[]>;
}

type ExtendedTrackCapabilities = MediaTrackCapabilities & {
  focusDistance?: { min: number; max: number; step: number };
  focusMode?: string[];
  torch?: boolean;
  zoom?: { min: number; max: number; step: number };
};

type ExtendedTrackConstraintSet = MediaTrackConstraintSet & {
  focusDistance?: number;
  focusMode?: string;
  zoom?: number;
};

type ExtendedTrackSettings = MediaTrackSettings & {
  focusDistance?: number;
  zoom?: number;
};

const emptySnapshot: TrackerSnapshot = {
  clients: [],
  client_rates: [],
  projects: [],
  work_sessions: [],
  food_items: [],
  food_logs: [],
  meal_presets: [],
  medication_logs: [],
  symptom_logs: [],
  location_rules: [],
  voice_notes: [],
  transcription_jobs: []
};

export default function App() {
  const [snapshot, setSnapshot] = useState<TrackerSnapshot>(emptySnapshot);
  const [tab, setTab] = useState<Tab>("work");
  const [online, setOnline] = useState(navigator.onLine);
  const [syncMessage, setSyncMessage] = useState("Verifica backend...");
  const [auth, setAuth] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [pwaWorker, setPwaWorker] = useState<PwaWorkerState>(() => getPwaWorkerState());
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(() => isStandaloneMode());
  const [syncDiagnostics, setSyncDiagnostics] = useState<SyncDiagnostics>({
    pending: 0,
    latestRev: 0,
    lastSyncAt: null,
    lastError: null,
    lastErrorKind: null
  });
  const reloadSequence = useRef(0);

  useEffect(() => {
    const updateWorker = () => setPwaWorker(getPwaWorkerState());
    const captureInstallPrompt = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as BeforeInstallPromptEvent);
    };
    const markInstalled = () => {
      setInstalled(true);
      setInstallPrompt(null);
    };
    const displayMode = window.matchMedia("(display-mode: standalone)");
    const updateDisplayMode = () => setInstalled(isStandaloneMode());

    updateWorker();
    window.addEventListener(PWA_STATUS_EVENT, updateWorker);
    window.addEventListener("beforeinstallprompt", captureInstallPrompt);
    window.addEventListener("appinstalled", markInstalled);
    displayMode.addEventListener?.("change", updateDisplayMode);
    return () => {
      window.removeEventListener(PWA_STATUS_EVENT, updateWorker);
      window.removeEventListener("beforeinstallprompt", captureInstallPrompt);
      window.removeEventListener("appinstalled", markInstalled);
      displayMode.removeEventListener?.("change", updateDisplayMode);
    };
  }, []);

  const reload = useCallback(async () => {
    const sequence = ++reloadSequence.current;
    const [nextSnapshot, diagnostics] = await Promise.all([loadSnapshot(), getSyncDiagnostics()]);
    if (sequence !== reloadSequence.current) {
      return;
    }
    setSnapshot(nextSnapshot);
    setPendingCount(diagnostics.pending);
    setSyncDiagnostics(diagnostics);
  }, []);

  useEffect(() => {
    reload().catch(() => undefined);
    getMe()
      .then((me) => {
        setAuth(me.authenticated);
        setAuthChecked(true);
        if (!me.authenticated) {
          setSyncMessage(navigator.onLine ? "Accesso richiesto" : "Offline");
        }
      })
      .catch((error: unknown) => {
        setAuth(false);
        setAuthChecked(true);
        setSyncMessage(navigator.onLine ? syncErrorMessage(error) : "Offline");
      });

    const onChange = () => reload().catch(() => undefined);
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener("tracker-db-changed", onChange);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    const timer = window.setInterval(onChange, 1_000);
    return () => {
      window.removeEventListener("tracker-db-changed", onChange);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      window.clearInterval(timer);
    };
  }, [reload]);

  const handleSync = useCallback(async () => {
    if (!navigator.onLine) {
      setSyncMessage("Offline");
      await reload();
      return;
    }
    setSyncMessage("Sync in corso...");
    try {
      const result = await syncNow();
      setSyncMessage(
        `Sync: completato alle ${new Intl.DateTimeFormat("it-IT", {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hourCycle: "h23"
        }).format(new Date(result.completedAt))}`
      );
      await reload();
      void registerBackgroundSync().catch(() => undefined);
    } catch (error: unknown) {
      if (error instanceof SyncError && error.kind === "auth") {
        setAuth(false);
        setAuthChecked(true);
      }
      setSyncMessage(syncErrorMessage(error));
      await reload();
    }
  }, [reload]);

  const installPwa = useCallback(async () => {
    if (!installPrompt) {
      return;
    }
    await installPrompt.prompt();
    const choice = await installPrompt.userChoice;
    if (choice.outcome === "accepted") {
      setInstallPrompt(null);
    }
  }, [installPrompt]);

  useEffect(() => {
    if (!online) {
      setSyncMessage("Offline");
      return;
    }
    if (authChecked && !auth) {
      setSyncMessage("Accesso richiesto");
      return;
    }
    if (!auth) {
      return;
    }

    let debounceTimer: number | undefined;
    const scheduleSync = () => {
      window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(() => handleSync(), 600);
    };
    handleSync();
    const interval = window.setInterval(handleSync, 30_000);
    window.addEventListener("tracker-sync-needed", scheduleSync);
    return () => {
      window.clearTimeout(debounceTimer);
      window.clearInterval(interval);
      window.removeEventListener("tracker-sync-needed", scheduleSync);
    };
  }, [auth, authChecked, online, handleSync]);

  return (
    <div className="app-shell">
      <header className="topbar">
        <div>
          <h1>Tracker Quotidiano</h1>
          <p>
            {syncMessage}
            {pendingCount > 0 ? ` · ${pendingCount} in coda` : ""}
          </p>
        </div>
        <div className="top-actions">
          <span className={`status ${online ? "online" : "offline"}`}>
            {online ? <Wifi size={16} /> : <WifiOff size={16} />}
            {online ? "Online" : "Offline"}
          </span>
          {!installed && installPrompt && (
            <button className="icon-button" type="button" onClick={installPwa} title="Installa app">
              <Download size={19} />
            </button>
          )}
          <button
            className="icon-button"
            type="button"
            onClick={auth ? handleSync : () => setTab("settings")}
            title={auth ? "Sincronizza" : "Accedi per sincronizzare"}
          >
            <RefreshCw size={19} />
          </button>
        </div>
      </header>
      {authChecked && !auth && pendingCount > 0 && (
        <div className="sync-alert" role="status">
          <span>
            <strong>{pendingCount} modifiche</strong> salvate su questo dispositivo.
          </span>
          <button type="button" onClick={() => setTab("settings")}>
            <LogIn size={17} />
            Accedi per sincronizzare
          </button>
        </div>
      )}

      <nav className="tabs" aria-label="Sezioni">
        <button className={tab === "work" ? "active" : ""} type="button" onClick={() => setTab("work")}>
          Lavoro
        </button>
        <button className={tab === "history" ? "active" : ""} type="button" onClick={() => setTab("history")}>
          Storico
        </button>
        <button className={tab === "invoice" ? "active" : ""} type="button" onClick={() => setTab("invoice")}>
          Pre-fattura
        </button>
        <button className={tab === "wellbeing" ? "active" : ""} type="button" onClick={() => setTab("wellbeing")}>
          Benessere
        </button>
        <button className={tab === "trends" ? "active" : ""} type="button" onClick={() => setTab("trends")}>
          Andamento
        </button>
        <button className={tab === "settings" ? "active" : ""} type="button" onClick={() => setTab("settings")}>
          Impostazioni
        </button>
      </nav>

      {tab === "work" && <WorkView snapshot={snapshot} />}
      {tab === "wellbeing" && <WellbeingView snapshot={snapshot} />}
      {tab === "trends" && <HealthTrendsView snapshot={snapshot} />}
      {tab === "history" && <HistoryView snapshot={snapshot} />}
      {tab === "invoice" && <InvoiceView snapshot={snapshot} />}
      {tab === "settings" && (
        <SettingsView
          snapshot={snapshot}
          authenticated={auth}
          diagnostics={syncDiagnostics}
          onAuthChange={setAuth}
          onSyncMessage={setSyncMessage}
          onSync={handleSync}
          pwaWorker={pwaWorker}
          installed={installed}
          installAvailable={Boolean(installPrompt)}
          onInstall={installPwa}
        />
      )}
    </div>
  );
}

function WorkView({ snapshot }: { snapshot: TrackerSnapshot }) {
  return (
    <main className="wide-content">
      <WorkPanel snapshot={snapshot} />
    </main>
  );
}

function WellbeingView({ snapshot }: { snapshot: TrackerSnapshot }) {
  return (
    <main className="dashboard-grid">
      <FoodPanel snapshot={snapshot} />
      <HealthPanel snapshot={snapshot} />
      <VoicePanel notes={active(snapshot.voice_notes).slice().reverse()} />
    </main>
  );
}

type HealthRange = "13" | "26" | "52" | "all";

const healthRangeOptions: Array<{ value: HealthRange; label: string }> = [
  { value: "13", label: "3 mesi" },
  { value: "26", label: "6 mesi" },
  { value: "52", label: "12 mesi" },
  { value: "all", label: "Tutto" }
];

function HealthTrendsView({ snapshot }: { snapshot: TrackerSnapshot }) {
  const [range, setRange] = useState<HealthRange>("26");
  const [selectedDate, setSelectedDate] = useState(todayKey());
  const heatmapRef = useRef<HTMLDivElement | null>(null);
  const days = useMemo(
    () => summarizeHealthDays(snapshot.medication_logs, snapshot.symptom_logs),
    [snapshot.medication_logs, snapshot.symptom_logs]
  );
  const daysByDate = useMemo(() => new Map(days.map((day) => [day.date, day])), [days]);
  const today = todayKey();
  const currentWeek = weekStartKey(today);
  const firstRecordedDate = days[0]?.date ?? today;
  const startWeek =
    range === "all"
      ? weekStartKey(firstRecordedDate)
      : addDaysKey(currentWeek, -(Number(range) - 1) * 7);
  const weekStarts: string[] = [];
  for (let week = startWeek; week <= currentWeek; week = addDaysKey(week, 7)) {
    weekStarts.push(week);
  }

  useEffect(() => {
    const container = heatmapRef.current;
    if (container) {
      container.scrollLeft = container.scrollWidth;
    }
  }, [range, weekStarts.length]);

  const periodDays = days.filter((day) => day.date >= startWeek && day.date <= today);
  const totalDoses = periodDays.reduce((total, day) => total + day.ipp_doses, 0);
  const oneDoseDays = periodDays.filter((day) => day.medication_recorded && day.ipp_doses === 1).length;
  const doubleDoseDays = periodDays.filter((day) => day.medication_recorded && day.ipp_doses >= 2).length;
  const vomitDays = periodDays.filter((day) => day.vomit_events > 0).length;
  const symptomEvents = periodDays.reduce(
    (total, day) => total + SYMPTOM_TREND_KINDS.reduce((count, kind) => count + (day.symptoms[kind] ?? 0), 0),
    0
  );
  const selected = daysByDate.get(selectedDate);

  return (
    <main className="wide-content">
      <section className="panel health-trends">
        <div className="health-trends-heading">
          <PanelTitle icon={<CalendarDays size={22} />} title="Andamento salute" />
          <div className="segmented-control range-selector" role="group" aria-label="Periodo andamento salute">
            {healthRangeOptions.map((option) => (
              <button
                className={range === option.value ? "active" : ""}
                type="button"
                key={option.value}
                aria-pressed={range === option.value}
                onClick={() => setRange(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <div className="health-metrics" aria-label="Riepilogo del periodo">
          <div>
            <strong>{totalDoses}</strong>
            <span>Dosi IPP</span>
          </div>
          <div>
            <strong>{oneDoseDays}</strong>
            <span>Giorni con 1</span>
          </div>
          <div>
            <strong>{doubleDoseDays}</strong>
            <span>Giorni con 2+</span>
          </div>
          <div>
            <strong>{vomitDays}</strong>
            <span>Giorni con episodio</span>
          </div>
          <div>
            <strong>{symptomEvents}</strong>
            <span>Sintomi</span>
          </div>
        </div>

        <div className="health-legend" aria-label="Legenda andamento salute">
          <HealthLegendItem className="dose-unknown" label="Nessun dato" />
          <HealthLegendItem className="dose-0 has-dose" label="0 dosi" />
          <HealthLegendItem className="dose-1 has-dose" label="1 dose" />
          <HealthLegendItem className="dose-2 has-dose" label="2+ dosi" />
          <HealthLegendItem className="vomit-1" label="Episodio" />
          <HealthLegendItem className="dose-1 has-dose vomit-1" label="Entrambi" />
          <HealthLegendItem className="anti-dot" label="F.Tidina" />
          <HealthLegendItem className="brioschi-dot" label="Brioschi" />
          <HealthLegendItem className="symptom-some" label="Sintomo" />
          <HealthLegendItem className="symptom-many" label="Sintomo ripetuto" />
          <span className="health-legend-note">Righe: Bruc=bruciore · Nodo=nodo stomaco · Ferm=fermentazione · Sed=dormito seduto</span>
        </div>

        <div className="health-heatmap-scroll" ref={heatmapRef}>
          <div
            className="health-heatmap"
            style={{ gridTemplateColumns: `38px repeat(${weekStarts.length}, 20px)` }}
          >
            <span className="health-grid-corner" aria-hidden="true" />
            {weekStarts.map((week, weekIndex) => {
              const previousWeek = weekIndex > 0 ? weekStarts[weekIndex - 1] : undefined;
              const showMonth = !previousWeek || monthKey(previousWeek) !== monthKey(week);
              return (
                <span
                  className="health-month-label"
                  key={`month-${week}`}
                  style={{ gridColumn: weekIndex + 2, gridRow: 1 }}
                >
                  {showMonth ? formatHealthMonth(week) : ""}
                </span>
              );
            })}
            {["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"].map((label, dayIndex) => (
              <span className="health-weekday" key={label} style={{ gridColumn: 1, gridRow: dayIndex + 2 }}>
                {label}
              </span>
            ))}
            {weekStarts.flatMap((week, weekIndex) =>
              Array.from({ length: 7 }, (_, dayIndex) => {
                const date = addDaysKey(week, dayIndex);
                const day = daysByDate.get(date);
                const isFuture = date > today;
                const doseClass = day?.medication_recorded
                  ? `dose-${Math.min(2, day.ipp_doses)}`
                  : "dose-unknown";
                const vomitClass = day?.vomit_events ? `vomit-${Math.min(2, day.vomit_events)}` : "";
                const brioschiClass = day && day.doses_by_kind.brioschi > 0 ? "brioschi" : "";
                return (
                  <button
                    className={`health-day ${doseClass} ${day?.medication_recorded ? "has-dose" : ""} ${vomitClass} ${brioschiClass} ${
                      isFuture ? "future" : ""
                    } ${selectedDate === date ? "selected" : ""}`}
                    type="button"
                    key={date}
                    style={{ gridColumn: weekIndex + 2, gridRow: dayIndex + 2 }}
                    disabled={isFuture}
                    aria-label={healthDayLabel(date, day)}
                    aria-pressed={selectedDate === date}
                    title={healthDayLabel(date, day)}
                    onClick={() => setSelectedDate(date)}
                  >
                    {day && day.doses_by_kind.antiacido > 0 && (
                      <span className="kind-dot kind-dot-anti" aria-hidden="true" />
                    )}
                  </button>
                );
              })
            )}
            {SYMPTOM_TREND_KINDS.flatMap((kind, kindIndex) => {
              const gridRow = kindIndex + 9;
              return [
                <span
                  className="health-symptom-label"
                  key={`symptom-label-${kind}`}
                  title={SYMPTOM_LABELS[kind]}
                  style={{ gridColumn: 1, gridRow }}
                >
                  {SYMPTOM_ROW_SHORT_LABELS[kind]}
                </span>,
                ...weekStarts.flatMap((week, weekIndex) =>
                  Array.from({ length: 7 }, (_, dayIndex) => {
                    const date = addDaysKey(week, dayIndex);
                    const count = daysByDate.get(date)?.symptoms[kind] ?? 0;
                    return (
                      <span
                        className={`health-symptom-cell ${count >= 2 ? "many" : count > 0 ? "some" : ""} ${
                          date > today ? "future" : ""
                        }`}
                        key={`symptom-${kind}-${date}`}
                        title={
                          count > 0
                            ? `${formatLongDate(date)} · ${SYMPTOM_LABELS[kind]} ×${count}`
                            : undefined
                        }
                        style={{ gridColumn: weekIndex + 2, gridRow }}
                      />
                    );
                  })
                )
              ];
            })}
          </div>
        </div>

        <div className="health-day-detail" aria-live="polite">
          <strong>{formatLongDate(selectedDate)}</strong>
          <span>
            IPP: {selected?.medication_recorded ? formatDoseCount(selected.ipp_doses) : "nessun dato"}
          </span>
          <span>
            F.Tidina: {selected ? formatDoseCount(selected.doses_by_kind.antiacido) : "no"} · Brioschi:{" "}
            {selected ? formatDoseCount(selected.doses_by_kind.brioschi) : "no"}
          </span>
          <span>Episodio gastrico: {selected?.vomit_events ? formatEventCount(selected.vomit_events) : "no"}</span>
          {selected && SYMPTOM_KINDS.some((kind) => selected.symptoms[kind]) && (
            <span>
              Sintomi:{" "}
              {SYMPTOM_KINDS.filter((kind) => selected.symptoms[kind])
                .map((kind) => `${SYMPTOM_LABELS[kind]} ×${selected.symptoms[kind]}`)
                .join(" · ")}
            </span>
          )}
        </div>
      </section>
    </main>
  );
}

function HealthLegendItem({ className, label }: { className: string; label: string }) {
  return (
    <span>
      <i className={`health-swatch ${className}`} aria-hidden="true" />
      {label}
    </span>
  );
}

function WorkPanel({ snapshot }: { snapshot: TrackerSnapshot }) {
  const clients = active(snapshot.clients);
  const projects = active(snapshot.projects);
  const sessions = active(snapshot.work_sessions);
  const activeSession = sessions.find((session) => !session.ended_at);
  const selectedClient = clients.find((client) => client.id === activeSession?.client_id);
  const selectedProject = projects.find((project) => project.id === activeSession?.project_id);
  const activeSessionId = activeSession?.id;
  const activeSessionNotes = activeSession?.notes;
  const selectedClientName = selectedClient?.name;
  const selectedProjectName = selectedProject?.name;
  const clientMap = new Map(clients.map((client) => [client.id, client]));
  const [clientName, setClientName] = useState("");
  const [projectName, setProjectName] = useState("");
  const [notes, setNotes] = useState("");
  const [message, setMessage] = useState("");
  const [switchingProjectId, setSwitchingProjectId] = useState<string | null>(null);

  const clientForSuggestions = clients.find((client) => client.name.toLowerCase() === clientName.toLowerCase()) ?? selectedClient;
  const projectSuggestions = clientForSuggestions ? rankProjects(projects, sessions, clientForSuggestions.id) : [];
  const recentProjects = rankRecentProjects(projects, sessions);
  const frequentProjects = rankFrequentProjects(projects, sessions);

  useEffect(() => {
    if (!activeSessionId) {
      return;
    }
    if (selectedClientName) setClientName(selectedClientName);
    if (selectedProjectName) setProjectName(selectedProjectName);
    setNotes(activeSessionNotes ?? "");
  }, [activeSessionId, activeSessionNotes, selectedClientName, selectedProjectName]);

  const start = async () => {
    if (!clientName.trim()) {
      setMessage("Inserisci un cliente.");
      return;
    }
    try {
      await startWorkSession(clientName, projectName || "Generale", notes);
      setMessage("Timer avviato.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Errore avvio timer.");
    }
  };

  const stop = async () => {
    const session = await getActiveWorkSession();
    if (!session) {
      setMessage("Nessun timer attivo.");
      return;
    }
    await stopWorkSession(session, notes);
    setNotes("");
    setMessage("Sessione chiusa.");
  };

  const suggestByLocation = async () => {
    if (!navigator.geolocation) {
      setMessage("Geolocalizzazione non disponibile.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const coordinates = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        };
        const rule = matchLocationRule(snapshot.location_rules, coordinates);
        if (rule) {
          const client = clients.find((item) => item.id === rule.client_id);
          if (client) {
            setClientName(client.name);
            setMessage(`Cliente suggerito: ${client.name}.`);
          }
          return;
        }
        if (!clientName.trim()) {
          setMessage("Seleziona un cliente e riprova per associare questa posizione.");
          return;
        }
        const client = await ensureClient(clientName);
        await saveLocationRule(client.id, client.name, coordinates);
        setMessage("Posizione associata al cliente.");
      },
      () => setMessage("Permesso posizione negato o posizione non rilevata."),
      { enableHighAccuracy: false, maximumAge: 60_000, timeout: 8_000 }
    );
  };

  const activeMinutes = activeSession
    ? Math.max(0, Math.floor((Date.now() - new Date(activeSession.started_at).getTime()) / 60_000))
    : 0;

  const selectProject = async (project: Project) => {
    const client = clientMap.get(project.client_id);
    if (!client) {
      return;
    }
    setClientName(client.name);
    setProjectName(project.name);
    if (!activeSession) {
      setMessage("");
      return;
    }
    if (switchingProjectId) {
      return;
    }

    setSwitchingProjectId(project.id);
    try {
      const result = await switchWorkSession(activeSession, client.name, project.name, notes);
      if (!result.closed) {
        setMessage("Questa attivita e gia in corso.");
        return;
      }
      setNotes("");
      setMessage(`Attivita cambiata: ${client.name} · ${project.name}.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Errore durante il cambio attivita.");
    } finally {
      setSwitchingProjectId(null);
    }
  };

  return (
    <section className="panel work-panel">
      <PanelTitle icon={<BriefcaseBusiness size={22} />} title="Lavoro" />
      <div className="timer-display">
        <div className="timer-elapsed">
          <strong>{activeSession ? formatRunning(activeSession.started_at) : "00:00"}</strong>
          <span>{activeSession ? `${formatDuration(activeMinutes)} registrati` : "Nessun timer attivo"}</span>
        </div>
        {activeSession && (
          <div className="timer-start-editor">
            <span>Inizio</span>
            <InlineValueEditor
              displayValue={formatTime(activeSession.started_at)}
              editValue={formatTime(activeSession.started_at)}
              label="Modifica inizio timer"
              hint="730 → 07:30"
              onCommit={async (value) => {
                const updated = await updateWorkSessionStartClock(activeSession, value);
                setMessage(`Inizio aggiornato alle ${formatTime(updated.started_at)}.`);
                return updated;
              }}
            />
          </div>
        )}
      </div>
      {(recentProjects.length > 0 || frequentProjects.length > 0) && (
        <div className="project-shortcuts">
          {recentProjects.length > 0 && (
            <div className="shortcut-group">
              <div className="shortcut-heading">
                <Clock size={16} />
                <span>Ultimi progetti</span>
              </div>
              <div className="quick-row">
                {recentProjects.map((project) => (
                  <ProjectShortcut
                    key={`recent-${project.id}`}
                    project={project}
                    client={clientMap.get(project.client_id)}
                    onSelect={selectProject}
                    disabled={Boolean(switchingProjectId)}
                  />
                ))}
              </div>
            </div>
          )}
          {frequentProjects.length > 0 && (
            <div className="shortcut-group">
              <div className="shortcut-heading">
                <TrendingUp size={16} />
                <span>Più usati</span>
              </div>
              <div className="quick-row">
                {frequentProjects.map((project) => (
                  <ProjectShortcut
                    key={`frequent-${project.id}`}
                    project={project}
                    client={clientMap.get(project.client_id)}
                    onSelect={selectProject}
                    disabled={Boolean(switchingProjectId)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      <div className="form-grid">
        <label>
          Cliente
          <input
            list="clients"
            value={clientName}
            onChange={(event) => setClientName(event.target.value)}
            placeholder="Nome cliente"
          />
        </label>
        <label>
          Progetto
          <input
            list="projects"
            value={projectName}
            onChange={(event) => setProjectName(event.target.value)}
            placeholder="Progetto"
          />
        </label>
      </div>
      <datalist id="clients">
        {clients.map((client) => (
          <option key={client.id} value={client.name} />
        ))}
      </datalist>
      <datalist id="projects">
        {projectSuggestions.map((project) => (
          <option key={project.id} value={project.name} />
        ))}
      </datalist>
      {projectSuggestions.length > 0 && (
        <div className="quick-row client-project-suggestions" aria-label="Progetti del cliente">
          {projectSuggestions.map((project) => (
            <button key={project.id} type="button" onClick={() => setProjectName(project.name)}>
              {project.name}
            </button>
          ))}
        </div>
      )}
      <label>
        Nota
        <textarea value={notes} onChange={(event) => setNotes(event.target.value)} rows={3} />
      </label>
      <div className="button-row">
        <button className="primary" type="button" onClick={activeSession ? stop : start}>
          {activeSession ? <CircleStop size={18} /> : <Play size={18} />}
          {activeSession ? "Ferma" : "Avvia"}
        </button>
        <button type="button" onClick={suggestByLocation}>
          <MapPin size={18} />
          Posizione
        </button>
      </div>
      {message && <p className="inline-message">{message}</p>}
    </section>
  );
}

function ProjectShortcut({
  project,
  client,
  onSelect,
  disabled = false
}: {
  project: Project;
  client?: Client;
  onSelect: (project: Project) => void | Promise<void>;
  disabled?: boolean;
}) {
  return (
    <button
      className="project-shortcut"
      type="button"
      disabled={disabled}
      onClick={() => void onSelect(project)}
    >
      <strong>{project.name}</strong>
      <span>{client?.name ?? "Cliente"}</span>
    </button>
  );
}

function FoodPanel({ snapshot }: { snapshot: TrackerSnapshot }) {
  const [foodName, setFoodName] = useState("");
  const [editingMeal, setEditingMeal] = useState<FoodMeal | null>(null);
  const [editingCourse, setEditingCourse] = useState<FoodLog | null>(null);
  const [targetMealId, setTargetMealId] = useState<string | null>(null);
  const [newMealType, setNewMealType] = useState<MealType>(() => inferMealType(new Date()));
  const [adding, setAdding] = useState(false);
  const [message, setMessage] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [managingPresets, setManagingPresets] = useState(false);
  const [editingPreset, setEditingPreset] = useState<{ slot: number; preset?: MealPreset } | null>(null);
  const [scanningBarcode, setScanningBarcode] = useState(false);
  const initialTargetSelected = useRef(false);
  const presetsInitialized = useRef(false);
  const quickFoods = rankFoodItems(snapshot.food_items, 6);
  const foodMatches = useMemo(
    () => searchFoodItems(snapshot.food_items, foodName, 6),
    [foodName, snapshot.food_items]
  );
  const foodMap = new Map(snapshot.food_items.map((item) => [item.id, item]));
  const meals = groupFoodLogsIntoMeals(snapshot.food_logs);
  const visibleMeals = showAll ? meals : meals.slice(0, 5);
  const targetMeal = meals.find((meal) => meal.id === targetMealId);
  const presets = active(snapshot.meal_presets).slice().sort((a, b) => a.slot - b.slot);
  const presetsBySlot = new Map(presets.map((preset) => [preset.slot, preset]));

  useEffect(() => {
    if (initialTargetSelected.current || !meals.length) {
      return;
    }
    initialTargetSelected.current = true;
    const latest = meals[0];
    const age = Date.now() - new Date(latest.eaten_at).getTime();
    if (localDateKey(latest.eaten_at) === localDateKey(new Date()) && age >= 0 && age <= 3 * 60 * 60_000) {
      setTargetMealId(latest.id);
    }
  }, [meals]);

  useEffect(() => {
    if (presetsInitialized.current || snapshot.meal_presets.length > 0 || snapshot.food_logs.length === 0) {
      return;
    }
    presetsInitialized.current = true;
    initializeMealPresets(snapshot.food_logs, snapshot.food_items).catch(() => {
      presetsInitialized.current = false;
    });
  }, [snapshot.food_items, snapshot.food_logs, snapshot.meal_presets.length]);

  const add = async (name = foodName) => {
    if (!name.trim() || adding) {
      return;
    }
    setAdding(true);
    try {
      const log = await logFood(
        name,
        "",
        targetMeal
          ? {
              id: targetMeal.id,
              eaten_at: targetMeal.eaten_at,
              meal_type: targetMeal.meal_type,
              notes: targetMeal.notes
            }
          : targetMealId
            ? { id: targetMealId, meal_type: newMealType }
            : { meal_type: newMealType }
      );
      if (log) {
        setTargetMealId(log.meal_id ?? null);
        setNewMealType(log.meal_type ?? newMealType);
        initialTargetSelected.current = true;
      }
      setFoodName("");
      setMessage(`Portata aggiunta ${mealTypeWithArticle(log?.meal_type ?? newMealType)}.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Registrazione non riuscita.");
    } finally {
      setAdding(false);
    }
  };

  const startNewMeal = () => {
    setTargetMealId(null);
    setNewMealType(inferMealType(new Date()));
    initialTargetSelected.current = true;
    setMessage("La prossima portata iniziera un nuovo pasto.");
  };

  const addPreset = async (preset: MealPreset) => {
    if (adding) {
      return;
    }
    setAdding(true);
    try {
      const logs = await logFoodGroup(
        preset.items,
        targetMeal
          ? {
              id: targetMeal.id,
              eaten_at: targetMeal.eaten_at,
              meal_type: targetMeal.meal_type,
              notes: targetMeal.notes
            }
          : targetMealId
            ? { id: targetMealId, meal_type: preset.meal_type }
            : { meal_type: preset.meal_type }
      );
      const first = logs[0];
      if (first) {
        setTargetMealId(first.meal_id ?? null);
        setNewMealType(first.meal_type ?? preset.meal_type);
        initialTargetSelected.current = true;
      }
      setMessage(`Gruppo ${preset.slot} aggiunto: ${logs.length} ${logs.length === 1 ? "portata" : "portate"}.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Inserimento del gruppo non riuscito.");
    } finally {
      setAdding(false);
    }
  };

  const selectMeal = (meal: FoodMeal) => {
    setTargetMealId(meal.id);
    initialTargetSelected.current = true;
    setMessage(`Le prossime portate verranno aggiunte ${mealTypeWithArticle(meal.meal_type)}.`);
  };

  const removeCourse = async (log: FoodLog) => {
    const name = foodMap.get(log.food_item_id)?.name ?? "questa portata";
    if (!window.confirm(`Eliminare ${name} dal pasto?`)) {
      return;
    }
    try {
      await deleteFoodLog(log);
      setMessage("Portata eliminata.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Eliminazione non riuscita.");
    }
  };

  const removeMeal = async (meal: FoodMeal) => {
    if (!window.confirm(`Eliminare ${mealTypeWithDefiniteArticle(meal.meal_type)} e tutte le sue portate?`)) {
      return;
    }
    try {
      await deleteFoodMeal(meal.id);
      if (targetMealId === meal.id) {
        setTargetMealId(null);
      }
      setMessage("Pasto eliminato.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Eliminazione non riuscita.");
    }
  };

  const closeBarcodeScanner = useCallback(() => setScanningBarcode(false), []);
  const useBarcodeDescription = useCallback((description: string) => {
    setFoodName(description);
    setScanningBarcode(false);
    setMessage("Descrizione trovata. Controllala prima di aggiungerla.");
  }, []);

  return (
    <section className="panel">
      <div className="panel-title-row">
        <PanelTitle icon={<Utensils size={22} />} title="Pasti" />
        <button
          className={`icon-button small panel-title-action${managingPresets ? " active" : ""}`}
          type="button"
          onClick={() => setManagingPresets((value) => !value)}
          title="Gestisci gruppi pasto"
          aria-label="Gestisci gruppi pasto"
          aria-expanded={managingPresets}
          aria-controls="meal-preset-manager"
        >
          <Settings2 size={16} />
        </button>
      </div>
      <div className="meal-target">
        <div>
          <span>Le portate saranno aggiunte a</span>
          {targetMeal ? (
            <strong>
              {mealTypeLabel(targetMeal.meal_type)} · {formatTime(targetMeal.eaten_at)} · {targetMeal.courses.length}{" "}
              {targetMeal.courses.length === 1 ? "portata" : "portate"}
            </strong>
          ) : (
            <label>
              <span className="sr-only">Tipo nuovo pasto</span>
              <select value={newMealType} onChange={(event) => setNewMealType(event.target.value as MealType)}>
                {mealTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    Nuovo: {option.label}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
        {targetMeal && (
          <button type="button" onClick={startNewMeal}>
            <Plus size={17} />
            Nuovo pasto
          </button>
        )}
      </div>
      <div className="meal-presets" aria-label="Gruppi pasto rapidi">
        <div className="meal-preset-buttons">
          {Array.from({ length: 10 }, (_, index) => index + 1).map((slot) => {
            const preset = presetsBySlot.get(slot);
            return (
              <button
                className={preset ? "meal-preset-number configured" : "meal-preset-number"}
                key={slot}
                type="button"
                onClick={() => preset && void addPreset(preset)}
                title={preset ? `${preset.name || `Gruppo ${slot}`}: ${preset.items.join(", ")}` : `Gruppo ${slot} non configurato`}
                aria-label={preset ? `Aggiungi gruppo ${slot}` : `Gruppo ${slot} non configurato`}
                disabled={adding || !preset}
              >
                {slot}
              </button>
            );
          })}
        </div>
        {managingPresets && (
          <div className="meal-preset-directory" id="meal-preset-manager">
            {Array.from({ length: 10 }, (_, index) => index + 1).map((slot) => {
              const preset = presetsBySlot.get(slot);
              return (
                <div key={preset?.id ?? slot}>
                  <strong>{slot}</strong>
                  <span>{preset ? `${preset.name ? `${preset.name} · ` : ""}${preset.items.join(" · ")}` : "Vuoto"}</span>
                  <button
                    className="icon-button small"
                    type="button"
                    onClick={() => setEditingPreset({ slot, preset })}
                    title={preset ? `Modifica gruppo ${slot}` : `Configura gruppo ${slot}`}
                    aria-label={preset ? `Modifica gruppo ${slot}` : `Configura gruppo ${slot}`}
                  >
                    {preset ? <Settings2 size={15} /> : <Plus size={15} />}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <div className="input-with-button">
        <input
          value={foodName}
          onChange={(event) => setFoodName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              void add();
            }
          }}
          placeholder="Portata o cibo"
          disabled={adding}
        />
        <button
          className="icon-button"
          type="button"
          onClick={() => setScanningBarcode(true)}
          title="Leggi codice a barre"
          aria-label="Leggi codice a barre"
          disabled={adding}
        >
          <ScanBarcode size={20} />
        </button>
        <button
          className="icon-button"
          type="button"
          onClick={() => add()}
          title="Aggiungi portata"
          disabled={adding || !foodName.trim()}
        >
          <Plus size={19} />
        </button>
      </div>
      {foodMatches.length > 0 && (
        <div className="food-search-results" aria-label="Portate corrispondenti">
          {foodMatches.map((item) => (
            <button key={item.id} type="button" onClick={() => add(item.name)} disabled={adding}>
              {item.name}
            </button>
          ))}
        </div>
      )}
      <div className="quick-row food-buttons">
        {quickFoods.map((item) => (
          <button key={item.id} type="button" onClick={() => add(item.name)} disabled={adding}>
            {item.name}
          </button>
        ))}
      </div>
      {message && <p className="inline-message">{message}</p>}
      <WellbeingListHeader
        title="Pasti registrati"
        count={meals.length}
        showAll={showAll}
        onToggle={() => setShowAll((value) => !value)}
      />
      <div className="meal-list">
        {meals.length === 0 && <span className="muted">Nessun pasto registrato</span>}
        {visibleMeals.map((meal) => (
          <article className={`meal-row${targetMealId === meal.id ? " selected" : ""}`} key={meal.id}>
            <header className="meal-row-heading">
              <div>
                <strong>{mealTypeLabel(meal.meal_type)}</strong>
                <span>
                  {formatWellbeingDateTime(meal.eaten_at)} · {meal.courses.length}{" "}
                  {meal.courses.length === 1 ? "portata" : "portate"}
                  {meal.notes ? ` · ${meal.notes}` : ""}
                </span>
              </div>
              <div className="row-actions">
                <button
                  className="icon-button small"
                  type="button"
                  onClick={() => selectMeal(meal)}
                  title={`Aggiungi portata ${mealTypeWithArticle(meal.meal_type)}`}
                  aria-label={`Aggiungi portata ${mealTypeWithArticle(meal.meal_type)}`}
                >
                  <Plus size={16} />
                </button>
                <button
                  className="icon-button small"
                  type="button"
                  onClick={() => setEditingMeal(meal)}
                  title={`Modifica ${mealTypeLabel(meal.meal_type).toLowerCase()}`}
                  aria-label={`Modifica ${mealTypeLabel(meal.meal_type).toLowerCase()}`}
                >
                  <Pencil size={16} />
                </button>
                <button
                  className="icon-button small danger-outline"
                  type="button"
                  onClick={() => removeMeal(meal)}
                  title={`Elimina ${mealTypeLabel(meal.meal_type).toLowerCase()}`}
                  aria-label={`Elimina ${mealTypeLabel(meal.meal_type).toLowerCase()}`}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </header>
            <div className="meal-courses">
              {meal.courses.map((course) => {
                const name = foodMap.get(course.food_item_id)?.name ?? "Cibo";
                return (
                  <div className="meal-course" key={course.id}>
                    <div>
                      <strong>{name}</strong>
                      {course.notes && <span>{course.notes}</span>}
                    </div>
                    <div className="row-actions">
                      <button
                        className="icon-button small"
                        type="button"
                        onClick={() => setEditingCourse(course)}
                        title={`Modifica portata ${name}`}
                        aria-label={`Modifica portata ${name}`}
                      >
                        <Pencil size={15} />
                      </button>
                      <button
                        className="icon-button small danger-outline"
                        type="button"
                        onClick={() => removeCourse(course)}
                        title={`Elimina portata ${name}`}
                        aria-label={`Elimina portata ${name}`}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </article>
        ))}
      </div>
      {editingMeal && (
        <EditFoodMealDialog
          meal={editingMeal}
          onClose={() => setEditingMeal(null)}
          onSaved={() => {
            setEditingMeal(null);
            setMessage("Pasto modificato.");
          }}
        />
      )}
      {editingCourse && (
        <EditFoodCourseDialog
          log={editingCourse}
          foodName={foodMap.get(editingCourse.food_item_id)?.name ?? ""}
          foods={quickFoods.map((item) => item.name)}
          onClose={() => setEditingCourse(null)}
          onSaved={() => {
            setEditingCourse(null);
            setMessage("Portata modificata.");
          }}
        />
      )}
      {editingPreset && (
        <MealPresetDialog
          slot={editingPreset.slot}
          preset={editingPreset.preset}
          onClose={() => setEditingPreset(null)}
          onSaved={() => {
            setEditingPreset(null);
            setMessage(`Gruppo ${editingPreset.slot} aggiornato.`);
          }}
        />
      )}
      {scanningBarcode && (
        <BarcodeScannerDialog onClose={closeBarcodeScanner} onFound={useBarcodeDescription} />
      )}
    </section>
  );
}

function MealPresetDialog({
  slot,
  preset,
  onClose,
  onSaved
}: {
  slot: number;
  preset?: MealPreset;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(preset?.name ?? "");
  const [mealType, setMealType] = useState<MealType>(preset?.meal_type ?? inferMealType(new Date()));
  const [items, setItems] = useState(preset?.items.join("\n") ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      await saveMealPreset(slot, name, mealType, items.split(/\r?\n/));
      onSaved();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Salvataggio non riuscito.");
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!preset || !window.confirm(`Eliminare il gruppo ${slot}?`)) {
      return;
    }
    setSaving(true);
    try {
      await deleteMealPreset(preset);
      onSaved();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Eliminazione non riuscita.");
      setSaving(false);
    }
  };

  return (
    <WellbeingDialogFrame title={`Gruppo pasto ${slot}`} onClose={onClose}>
      <form className="modal-body" onSubmit={save}>
        <label>
          Nome
          <input autoFocus value={name} onChange={(event) => setName(event.target.value)} placeholder={`Gruppo ${slot}`} />
        </label>
        <label>
          Tipo pasto
          <select value={mealType} onChange={(event) => setMealType(event.target.value as MealType)}>
            {mealTypeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Portate, una per riga
          <textarea
            rows={7}
            value={items}
            onChange={(event) => setItems(event.target.value)}
            placeholder={"Cracker integrali\nMarmellata\nTè"}
          />
        </label>
        {error && <p className="form-error">{error}</p>}
        <div className="modal-actions">
          {preset && (
            <button className="danger-outline" type="button" onClick={remove} disabled={saving}>
              <Trash2 size={17} />
              Elimina
            </button>
          )}
          <button type="button" onClick={onClose} disabled={saving}>
            Annulla
          </button>
          <button className="primary" type="submit" disabled={saving || !items.trim()}>
            <Save size={17} />
            Salva
          </button>
        </div>
      </form>
    </WellbeingDialogFrame>
  );
}

function BarcodeScannerDialog({ onClose, onFound }: { onClose: () => void; onFound: (description: string) => void }) {
  const [barcode, setBarcode] = useState("");
  const [message, setMessage] = useState("Avvio fotocamera...");
  const [searching, setSearching] = useState(false);
  const [cameraStage, setCameraStage] = useState("inizializzazione");
  const [cameraEvents, setCameraEvents] = useState<string[]>([]);
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const [diagnosticsCopied, setDiagnosticsCopied] = useState(false);
  const [zoomRange, setZoomRange] = useState<{ min: number; max: number; step: number } | null>(null);
  const [zoomValue, setZoomValue] = useState(1);
  const [focusRange, setFocusRange] = useState<{ min: number; max: number; step: number } | null>(null);
  const [focusValue, setFocusValue] = useState(0);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const controlsRef = useRef<IScannerControls | null>(null);
  const cameraTrackRef = useRef<MediaStreamTrack | null>(null);
  const detectedRef = useRef(false);
  const candidatesRef = useRef(new Map<string, BarcodeCandidate>());
  const candidateCooldownsRef = useRef(new Map<string, number>());
  const onFoundRef = useRef(onFound);

  useEffect(() => {
    onFoundRef.current = onFound;
  }, [onFound]);

  const recordCameraEvent = useCallback((event: string) => {
    const time = new Date().toLocaleTimeString("it-IT", { hour12: false });
    setCameraEvents((current) => [...current.slice(-11), `${time} ${event}`]);
  }, []);

  const updateZoom = useCallback(
    async (value: number) => {
      setZoomValue(value);
      const track = cameraTrackRef.current;
      if (!track) {
        return;
      }
      try {
        const constraint: ExtendedTrackConstraintSet = { zoom: value };
        await track.applyConstraints({ advanced: [constraint] });
        recordCameraEvent(`zoom impostato a ${value.toFixed(1)}x`);
      } catch (error: unknown) {
        recordCameraEvent(`zoom non applicato: ${error instanceof Error ? error.message : String(error)}`);
      }
    },
    [recordCameraEvent]
  );

  const updateFocus = useCallback(
    async (value: number) => {
      setFocusValue(value);
      const track = cameraTrackRef.current;
      if (!track) {
        return;
      }
      try {
        const constraint: ExtendedTrackConstraintSet = { focusMode: "manual", focusDistance: value };
        await track.applyConstraints({ advanced: [constraint] });
        recordCameraEvent(`fuoco manuale impostato a ${value.toFixed(2)}`);
      } catch (error: unknown) {
        recordCameraEvent(`fuoco non applicato: ${error instanceof Error ? error.message : String(error)}`);
      }
    },
    [recordCameraEvent]
  );

  const search = useCallback(
    async (value: string) => {
      const normalized = value.replace(/\D/g, "");
      setBarcode(normalized);
      setSearching(true);
      setMessage("Ricerca prodotto...");
      try {
        const product = await lookupBarcode(normalized);
        controlsRef.current?.stop();
        controlsRef.current = null;
        onFoundRef.current(product.description);
      } catch (error) {
        detectedRef.current = false;
        candidatesRef.current.delete(normalized);
        candidateCooldownsRef.current.set(normalized, Date.now() + 3_000);
        recordCameraEvent(`ricerca non riuscita per ${normalized}`);
        setMessage(error instanceof Error ? error.message : "Prodotto non trovato.");
        setSearching(false);
      }
    },
    [recordCameraEvent]
  );

  const acceptDetectedBarcode = useCallback(
    (rawValue: string, source: string) => {
      if (detectedRef.current) {
        return;
      }
      const value = normalizeDetectedBarcode(rawValue);
      if (!value) {
        return;
      }
      const now = Date.now();
      if ((candidateCooldownsRef.current.get(value) ?? 0) > now) {
        return;
      }
      const observation = registerBarcodeCandidate(candidatesRef.current, value, source, now);
      setBarcode(value);
      if (!observation.confirmed) {
        if (observation.candidate.count === 1) {
          recordCameraEvent(`candidato da ${source}: ${value}`);
        }
        setMessage(
          `Verifica lettura ${Math.min(observation.candidate.count, observation.requiredCount)}/${
            observation.requiredCount
          }: mantieni fermo.`
        );
        return;
      }
      detectedRef.current = true;
      recordCameraEvent(
        `codice confermato: ${value} (${observation.candidate.count} letture, ${observation.candidate.sources.size} decoder)`
      );
      void search(value);
    },
    [recordCameraEvent, search]
  );

  useEffect(() => {
    let disposed = false;
    let localControls: IScannerControls | null = null;
    let localStream: MediaStream | null = null;
    let nativeDetectionTimer: number | null = null;
    recordCameraEvent("richiesta fotocamera posteriore");
    const startScanner = async () => {
      try {
        const [{ BrowserMultiFormatReader }, { BarcodeFormat, DecodeHintType }] = await Promise.all([
          import("@zxing/browser"),
          import("@zxing/library")
        ]);
        if (disposed) {
          return;
        }
        const hints = new Map<number, unknown>();
        hints.set(DecodeHintType.POSSIBLE_FORMATS, [
          BarcodeFormat.EAN_13,
          BarcodeFormat.EAN_8,
          BarcodeFormat.CODE_128,
          BarcodeFormat.CODE_39,
          BarcodeFormat.CODE_93,
          BarcodeFormat.CODABAR,
          BarcodeFormat.ITF,
          BarcodeFormat.RSS_14,
          BarcodeFormat.RSS_EXPANDED,
          BarcodeFormat.UPC_A,
          BarcodeFormat.UPC_E
        ]);
        hints.set(DecodeHintType.TRY_HARDER, true);
        const reader = new BrowserMultiFormatReader(hints, { delayBetweenScanAttempts: 180 });
        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error("getUserMedia non disponibile");
        }
        localStream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: "environment" },
            width: { ideal: 1280 },
            height: { ideal: 720 },
            frameRate: { ideal: 30 }
          },
          audio: false
        });
        if (disposed) {
          localStream.getTracks().forEach((track) => track.stop());
          return;
        }
        const track = localStream.getVideoTracks()[0];
        cameraTrackRef.current = track;
        const capabilities = track?.getCapabilities() as ExtendedTrackCapabilities | undefined;
        if (capabilities) {
          const focusModes = capabilities.focusMode?.join(",") || "n/d";
          const focusDistance = capabilities.focusDistance
            ? `${capabilities.focusDistance.min}-${capabilities.focusDistance.max}`
            : "n/d";
          const zoom = capabilities.zoom ? `${capabilities.zoom.min}-${capabilities.zoom.max}` : "n/d";
          recordCameraEvent(
            `capacita focus=${focusModes} distanza=${focusDistance} zoom=${zoom} torcia=${capabilities.torch ? "si" : "no"}`
          );
          if (capabilities.focusMode?.includes("continuous")) {
            try {
              const focusConstraint: ExtendedTrackConstraintSet = { focusMode: "continuous" };
              await track.applyConstraints({ advanced: [focusConstraint] });
              recordCameraEvent("autofocus continuo applicato");
            } catch (error: unknown) {
              recordCameraEvent(`autofocus non applicato: ${error instanceof Error ? error.message : String(error)}`);
            }
          }
          const currentSettings = track.getSettings() as ExtendedTrackSettings;
          if (capabilities.focusDistance && currentSettings.focusDistance !== undefined) {
            setFocusRange(capabilities.focusDistance);
            setFocusValue(currentSettings.focusDistance);
          }
          if (capabilities.zoom && capabilities.zoom.max > capabilities.zoom.min) {
            const preferredZoom = Math.min(capabilities.zoom.max, Math.max(capabilities.zoom.min, 2));
            setZoomRange(capabilities.zoom);
            await updateZoom(preferredZoom);
          }
        }
        localControls = await reader.decodeFromStream(
          localStream,
          videoRef.current ?? undefined,
          (result) => {
            if (!result) {
              return;
            }
            acceptDetectedBarcode(result.getText(), "ZXing");
          }
        );
        if (disposed) {
          localControls.stop();
          return;
        }
        controlsRef.current = localControls;
        const settings = track?.getSettings();
        setCameraStage("attiva");
        setMessage("Inquadra il codice a barre.");
        recordCameraEvent(
          `stream attivo${settings?.width && settings?.height ? ` ${settings.width}x${settings.height}` : ""}${
            settings?.frameRate ? ` ${Math.round(settings.frameRate)} fps` : ""
          }`
        );
        const Detector = (globalThis as typeof globalThis & { BarcodeDetector?: NativeBarcodeDetectorConstructor })
          .BarcodeDetector;
        if (!Detector) {
          recordCameraEvent("decoder nativo non disponibile; uso ZXing");
        } else {
          try {
            const supportedFormats = await Detector.getSupportedFormats();
            if (disposed) {
              return;
            }
            const wantedFormats = [
              "ean_13",
              "ean_8",
              "upc_a",
              "upc_e",
              "code_128",
              "code_39",
              "code_93",
              "codabar",
              "itf"
            ].filter((format) => supportedFormats.includes(format));
            const detector = new Detector(wantedFormats.length ? { formats: wantedFormats } : undefined);
            recordCameraEvent(`decoder nativo attivo: ${wantedFormats.join(",") || "formati predefiniti"}`);
            const detectNative = async () => {
              if (disposed) {
                return;
              }
              if (detectedRef.current) {
                nativeDetectionTimer = window.setTimeout(() => void detectNative(), 260);
                return;
              }
              const video = videoRef.current;
              if (video && video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
                try {
                  const results = await detector.detect(video);
                  const result = results.find((candidate) => /^\d{8,14}$/.test(candidate.rawValue.replace(/\D/g, "")));
                  if (result) {
                    acceptDetectedBarcode(result.rawValue, `decoder nativo ${result.format}`);
                  }
                } catch (error: unknown) {
                  recordCameraEvent(`decoder nativo in errore: ${error instanceof Error ? error.message : String(error)}`);
                  return;
                }
              }
              nativeDetectionTimer = window.setTimeout(() => void detectNative(), 260);
            };
            nativeDetectionTimer = window.setTimeout(() => void detectNative(), 100);
          } catch (error: unknown) {
            recordCameraEvent(`decoder nativo non inizializzato: ${error instanceof Error ? error.message : String(error)}`);
          }
        }
      } catch (error: unknown) {
        if (disposed) {
          return;
        }
        const detail = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
        setCameraStage("errore");
        recordCameraEvent(`errore ${detail}`);
        setMessage("Fotocamera non disponibile. Inserisci il codice.");
      }
    };
    void startScanner();
    return () => {
      disposed = true;
      localControls?.stop();
      localStream?.getTracks().forEach((track) => track.stop());
      if (nativeDetectionTimer !== null) {
        window.clearTimeout(nativeDetectionTimer);
      }
      if (controlsRef.current === localControls) {
        controlsRef.current = null;
      }
      if (cameraTrackRef.current === localStream?.getVideoTracks()[0]) {
        cameraTrackRef.current = null;
      }
    };
  }, [acceptDetectedBarcode, recordCameraEvent, updateZoom]);

  const diagnosticText = buildBarcodeCameraDiagnostics(cameraStage, videoRef.current, cameraEvents);

  const copyDiagnostics = async () => {
    try {
      await navigator.clipboard.writeText(diagnosticText);
      setDiagnosticsCopied(true);
    } catch {
      setMessage("Copia non riuscita. Seleziona manualmente la diagnostica.");
    }
  };

  return (
    <WellbeingDialogFrame title="Codice a barre" onClose={onClose}>
      <form
        className="modal-body barcode-scanner"
        onSubmit={(event) => {
          event.preventDefault();
          void search(barcode);
        }}
      >
        <div className="barcode-preview">
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            aria-label="Anteprima fotocamera"
            onLoadedMetadata={() => recordCameraEvent("metadati video caricati")}
            onPlaying={() => {
              setCameraStage("riproduzione");
              recordCameraEvent("anteprima in riproduzione");
            }}
            onWaiting={() => recordCameraEvent("anteprima in attesa")}
            onStalled={() => recordCameraEvent("anteprima bloccata")}
            onPause={() => recordCameraEvent("anteprima in pausa")}
            onError={() => recordCameraEvent("errore elemento video")}
          />
          <span className="barcode-scan-guide" aria-hidden="true" />
        </div>
        {(zoomRange || focusRange) && (
          <div className="barcode-camera-controls">
            {zoomRange && (
              <label>
                <span>Zoom</span>
                <input
                  type="range"
                  min={zoomRange.min}
                  max={zoomRange.max}
                  step={zoomRange.step || 0.1}
                  value={zoomValue}
                  onChange={(event) => void updateZoom(Number(event.target.value))}
                />
                <output>{zoomValue.toFixed(1)}x</output>
              </label>
            )}
            {focusRange && (
              <label>
                <span>Fuoco</span>
                <input
                  type="range"
                  min={focusRange.min}
                  max={focusRange.max}
                  step={focusRange.step || 0.01}
                  value={focusValue}
                  onChange={(event) => void updateFocus(Number(event.target.value))}
                />
                <output>{focusValue.toFixed(2)}</output>
              </label>
            )}
          </div>
        )}
        <label>
          Codice a barre
          <input
            inputMode="numeric"
            autoComplete="off"
            value={barcode}
            onChange={(event) => setBarcode(event.target.value.replace(/\D/g, ""))}
          />
        </label>
        <div className="barcode-status">
          <p className={message.includes("non") || message.includes("richiede") ? "form-error" : "inline-message"}>
            {message}
          </p>
          <button
            className={`icon-button small${showDiagnostics ? " active" : ""}`}
            type="button"
            onClick={() => setShowDiagnostics((value) => !value)}
            title="Diagnostica fotocamera"
            aria-label="Diagnostica fotocamera"
            aria-expanded={showDiagnostics}
          >
            <Activity size={16} />
          </button>
        </div>
        {showDiagnostics && (
          <div className="barcode-diagnostics">
            <pre>{diagnosticText}</pre>
            <button type="button" onClick={() => void copyDiagnostics()}>
              <ClipboardCopy size={16} />
              {diagnosticsCopied ? "Copiata" : "Copia diagnostica"}
            </button>
          </div>
        )}
        <div className="modal-actions">
          <button type="button" onClick={onClose}>
            Annulla
          </button>
          <button className="primary" type="submit" disabled={searching || !/^\d{8,14}$/.test(barcode)}>
            <ScanBarcode size={17} />
            Cerca
          </button>
        </div>
      </form>
    </WellbeingDialogFrame>
  );
}

function buildBarcodeCameraDiagnostics(stage: string, video: HTMLVideoElement | null, events: string[]): string {
  const stream = video?.srcObject instanceof MediaStream ? video.srcObject : null;
  const track = stream?.getVideoTracks()[0];
  const settings = track?.getSettings() as ExtendedTrackSettings | undefined;
  return JSON.stringify(
    {
      timestamp: new Date().toISOString(),
      stage,
      secureContext: window.isSecureContext,
      visibility: document.visibilityState,
      userAgent: navigator.userAgent,
      video: video
        ? {
            readyState: video.readyState,
            paused: video.paused,
            videoWidth: video.videoWidth,
            videoHeight: video.videoHeight,
            currentTime: Number(video.currentTime.toFixed(2))
          }
        : null,
      stream: stream
        ? {
            active: stream.active,
            trackState: track?.readyState,
            trackMuted: track?.muted,
            settings: settings
              ? {
                  deviceId: settings.deviceId,
                  facingMode: settings.facingMode,
                  width: settings.width,
                  height: settings.height,
                  frameRate: settings.frameRate,
                  zoom: settings.zoom,
                  focusDistance: settings.focusDistance
                }
              : null
          }
        : null,
      events
    },
    null,
    2
  );
}

function HealthPanel({ snapshot }: { snapshot: TrackerSnapshot }) {
  const todayIpp = getTodayMedication(snapshot.medication_logs, "ipp");
  const todayIppDoses = todayIpp ? medicationDoseCount(todayIpp) : undefined;
  const [notes, setNotes] = useState("");
  const [editingMedication, setEditingMedication] = useState<MedicationLog | null>(null);
  const [editingSymptom, setEditingSymptom] = useState<SymptomLog | null>(null);
  const [message, setMessage] = useState("");
  const [showAll, setShowAll] = useState(false);
  const entries = [
    ...active(snapshot.medication_logs).map((log) => ({
      kind: "medication" as const,
      at: log.taken_at ?? `${log.date}T12:00:00`,
      log
    })),
    ...active(snapshot.symptom_logs).map((log) => ({
      kind: "symptom" as const,
      at: log.occurred_at,
      log
    }))
  ].sort((a, b) => b.at.localeCompare(a.at));
  const visibleEntries = showAll ? entries : entries.slice(0, 5);

  const setDose = async (kind: MedicationKind, doseCount: number) => {
    await setMedicationDose(todayKey(), kind, doseCount);
    setMessage(
      doseCount === 0
        ? `${MEDICATION_LABELS[kind]}: non preso oggi.`
        : `${MEDICATION_LABELS[kind]} registrato: ${formatDoseCount(doseCount)}.`
    );
    if (kind === "ipp" && doseCount > 0 && "Notification" in window && Notification.permission === "default") {
      await Notification.requestPermission().catch(() => undefined);
    }
  };

  const symptom = async (kind: SymptomKind) => {
    await logSymptom(kind, notes);
    setNotes("");
    setMessage(`${SYMPTOM_LABELS[kind]} registrato.`);
  };

  const removeMedication = async (log: MedicationLog) => {
    if (!window.confirm(`Eliminare questa registrazione di ${MEDICATION_LABELS[normalizeMedicationKind(log.medication)]}?`)) {
      return;
    }
    try {
      await deleteMedicationLog(log);
      setMessage("Registrazione farmaco eliminata.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Eliminazione non riuscita.");
    }
  };

  const removeSymptom = async (log: SymptomLog) => {
    if (!window.confirm("Eliminare questa registrazione del sintomo?")) {
      return;
    }
    try {
      await deleteSymptomLog(log);
      setMessage("Sintomo eliminato.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Eliminazione non riuscita.");
    }
  };

  return (
    <section className="panel">
      <PanelTitle icon={<Pill size={22} />} title="Salute" />
      {todayIppDoses === undefined && <div className="reminder">IPP non registrato oggi.</div>}
      {todayIppDoses === 0 && <div className="reminder neutral">Oggi: nessun IPP.</div>}
      <div className="medication-controls">
        {MEDICATION_KINDS.map((kind) => {
          const log = getTodayMedication(snapshot.medication_logs, kind);
          const todayDoseCount = log ? medicationDoseCount(log) : undefined;
          return (
            <div className="dose-control" key={kind}>
              <span>{MEDICATION_LABELS[kind]}</span>
              <div
                className="segmented-control dose-selector"
                role="group"
                aria-label={`Dosi ${MEDICATION_LABELS[kind]} oggi`}
              >
                {[0, 1, 2].map((doseCount) => (
                  <button
                    className={todayDoseCount === doseCount ? "active" : ""}
                    type="button"
                    key={doseCount}
                    aria-pressed={todayDoseCount === doseCount}
                    aria-label={`${doseCount} ${doseCount === 1 ? "dose" : "dosi"} ${MEDICATION_LABELS[kind]} oggi`}
                    onClick={() => setDose(kind, doseCount)}
                  >
                    {doseCount}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <div className="symptom-chips" role="group" aria-label="Registra un sintomo">
        {SYMPTOM_KINDS.map((kind) => (
          <button type="button" key={kind} onClick={() => symptom(kind)}>
            {SYMPTOM_LABELS[kind]}
          </button>
        ))}
      </div>
      <label>
        Note
        <textarea
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          rows={2}
          placeholder="Dettagli opzionali per il sintomo"
        />
      </label>
      {message && <p className="inline-message">{message}</p>}
      <WellbeingListHeader
        title="Salute registrata"
        count={entries.length}
        showAll={showAll}
        onToggle={() => setShowAll((value) => !value)}
      />
      <div className="wellbeing-list">
        {entries.length === 0 && <span className="muted">Nessuna registrazione sanitaria</span>}
        {visibleEntries.map((entry) => {
          if (entry.kind === "medication") {
            const log = entry.log;
            const kind = normalizeMedicationKind(log.medication);
            return (
              <div className="wellbeing-row" key={`medication-${log.id}`}>
                <div>
                  <strong>
                    {MEDICATION_LABELS[kind]}: {formatDoseCount(medicationDoseCount(log))}
                  </strong>
                  <span>
                    {formatDateKey(log.date)}
                    {log.taken_at ? ` · ${formatTime(log.taken_at)}` : ""}
                  </span>
                </div>
                <div className="row-actions">
                  <button
                    className="icon-button small"
                    type="button"
                    onClick={() => setEditingMedication(log)}
                    title={`Modifica ${MEDICATION_LABELS[kind]} ${formatDateKey(log.date)}`}
                    aria-label={`Modifica ${MEDICATION_LABELS[kind]} ${formatDateKey(log.date)}`}
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    className="icon-button small danger-outline"
                    type="button"
                    onClick={() => removeMedication(log)}
                    title={`Elimina ${MEDICATION_LABELS[kind]} ${formatDateKey(log.date)}`}
                    aria-label={`Elimina ${MEDICATION_LABELS[kind]} ${formatDateKey(log.date)}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            );
          }
          const log = entry.log;
          return (
            <div className="wellbeing-row" key={`symptom-${log.id}`}>
              <div>
                <strong>{symptomLogLabel(log)}</strong>
                <span>
                  {formatWellbeingDateTime(log.occurred_at)}
                  {log.notes ? ` · ${log.notes}` : ""}
                </span>
              </div>
              <div className="row-actions">
                <button
                  className="icon-button small"
                  type="button"
                  onClick={() => setEditingSymptom(log)}
                  title={`Modifica ${symptomLogLabel(log)}`}
                  aria-label={`Modifica ${symptomLogLabel(log)}`}
                >
                  <Pencil size={16} />
                </button>
                <button
                  className="icon-button small danger-outline"
                  type="button"
                  onClick={() => removeSymptom(log)}
                  title={`Elimina ${symptomLogLabel(log)}`}
                  aria-label={`Elimina ${symptomLogLabel(log)}`}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
      {editingMedication && (
        <EditMedicationLogDialog
          log={editingMedication}
          onClose={() => setEditingMedication(null)}
          onSaved={() => {
            setEditingMedication(null);
            setMessage("Registrazione modificata.");
          }}
        />
      )}
      {editingSymptom && (
        <EditSymptomLogDialog
          log={editingSymptom}
          onClose={() => setEditingSymptom(null)}
          onSaved={() => {
            setEditingSymptom(null);
            setMessage("Sintomo modificato.");
          }}
        />
      )}
    </section>
  );
}

function VoicePanel({ notes }: { notes: VoiceNote[] }) {
  const [recording, setRecording] = useState(false);
  const [message, setMessage] = useState("");
  const chunksRef = useRef<Blob[]>([]);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const startedAtRef = useRef<number>(0);
  const [editing, setEditing] = useState<VoiceNote | null>(null);
  const [showAll, setShowAll] = useState(false);
  const visibleNotes = showAll ? notes : notes.slice(0, 5);

  const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setMessage("Microfono non disponibile.");
      return;
    }
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const mimeType = preferredAudioMimeType();
    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    chunksRef.current = [];
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        chunksRef.current.push(event.data);
      }
    };
    recorder.onstop = async () => {
      const blob = new Blob(chunksRef.current, { type: mimeType || recorder.mimeType || "audio/webm" });
      const duration = Math.max(1, Math.round((Date.now() - startedAtRef.current) / 1_000));
      await saveVoiceNote(blob, blob.type || "audio/webm", duration);
      stream.getTracks().forEach((track) => track.stop());
      setMessage("Nota vocale salvata.");
      setRecording(false);
    };
    recorderRef.current = recorder;
    streamRef.current = stream;
    startedAtRef.current = Date.now();
    recorder.start(10_000);
    setRecording(true);
    setMessage("Registrazione in corso.");
  };

  const stopRecording = () => {
    recorderRef.current?.stop();
    streamRef.current?.getTracks().forEach((track) => track.stop());
  };

  const remove = async (note: VoiceNote) => {
    if (!window.confirm(`Eliminare la nota vocale "${note.title}"?`)) {
      return;
    }
    try {
      await deleteVoiceNote(note);
      setMessage("Nota vocale eliminata.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Eliminazione non riuscita.");
    }
  };

  return (
    <section className="panel">
      <PanelTitle icon={<Mic size={22} />} title="Voce" />
      <button className={recording ? "danger wide" : "primary wide"} type="button" onClick={recording ? stopRecording : startRecording}>
        {recording ? <Square size={18} /> : <Mic size={18} />}
        {recording ? "Ferma registrazione" : "Registra nota"}
      </button>
      {message && <p className="inline-message">{message}</p>}
      <WellbeingListHeader
        title="Note registrate"
        count={notes.length}
        showAll={showAll}
        onToggle={() => setShowAll((value) => !value)}
      />
      <div className="wellbeing-list">
        {visibleNotes.map((note) => (
          <div className="wellbeing-row" key={note.id}>
            <div>
              <strong>{note.title}</strong>
              <span>
                {formatWellbeingDateTime(note.created_at)} · {transcriptionLabel(note.transcription_status)}
                {note.transcript ? ` · ${note.transcript}` : ""}
              </span>
            </div>
            <div className="row-actions">
              <button
                className="icon-button small"
                type="button"
                onClick={() => setEditing(note)}
                title={`Modifica nota vocale ${note.title}`}
                aria-label={`Modifica nota vocale ${note.title}`}
              >
                <Pencil size={16} />
              </button>
              <button
                className="icon-button small danger-outline"
                type="button"
                onClick={() => remove(note)}
                title={`Elimina nota vocale ${note.title}`}
                aria-label={`Elimina nota vocale ${note.title}`}
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
        {notes.length === 0 && <span className="muted">Nessuna nota vocale</span>}
      </div>
      {editing && (
        <EditVoiceNoteDialog
          note={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            setMessage("Nota vocale modificata.");
          }}
        />
      )}
    </section>
  );
}

function WellbeingListHeader({
  title,
  count,
  showAll,
  onToggle
}: {
  title: string;
  count: number;
  showAll: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="wellbeing-list-heading">
      <strong>
        {title} <span>{count}</span>
      </strong>
      {count > 5 && (
        <button type="button" onClick={onToggle}>
          {showAll ? "Riduci" : "Mostra tutti"}
        </button>
      )}
    </div>
  );
}

function WellbeingDialogFrame({
  title,
  onClose,
  children
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="modal compact-modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="modal-heading">
          <div>
            <span className="eyebrow">Benessere</span>
            <h2>{title}</h2>
          </div>
          <button className="icon-button" type="button" onClick={onClose} title="Chiudi">
            <X size={20} />
          </button>
        </div>
        {children}
      </section>
    </div>
  );
}

function EditFoodMealDialog({
  meal,
  onClose,
  onSaved
}: {
  meal: FoodMeal;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [mealType, setMealType] = useState<MealType>(meal.meal_type);
  const [date, setDate] = useState(formatDateInput(localDateKey(meal.eaten_at)));
  const [time, setTime] = useState(formatTime(meal.eaten_at));
  const [notes, setNotes] = useState(meal.notes);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    setSaving(true);
    try {
      await updateFoodMeal(meal.id, dateTimeFromInputs(date, time), mealType, notes);
      onSaved();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Modifica non riuscita.");
      setSaving(false);
    }
  };

  return (
    <WellbeingDialogFrame title="Modifica pasto" onClose={onClose}>
      <form className="modal-body" onSubmit={save}>
        <label>
          Tipo pasto
          <select autoFocus value={mealType} onChange={(event) => setMealType(event.target.value as MealType)}>
            {mealTypeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <div className="form-grid">
          <label>
            Data
            <ItalianDateInput value={date} onChange={setDate} label="Data pasto" />
          </label>
          <label>
            Ora
            <input
              inputMode="numeric"
              value={time}
              onChange={(event) => setTime(event.target.value)}
              aria-label="Ora pasto"
            />
          </label>
        </div>
        <label>
          Nota del pasto
          <textarea rows={3} value={notes} onChange={(event) => setNotes(event.target.value)} />
        </label>
        <p className="muted compact-copy">
          La nuova data e ora verranno applicate a tutte le {meal.courses.length} portate.
        </p>
        {error && <p className="form-error">{error}</p>}
        <div className="modal-actions">
          <button type="button" onClick={onClose}>
            Annulla
          </button>
          <button className="primary" type="submit" disabled={saving}>
            <Save size={17} />
            {saving ? "Salvataggio..." : "Salva"}
          </button>
        </div>
      </form>
    </WellbeingDialogFrame>
  );
}

function EditFoodCourseDialog({
  log,
  foodName,
  foods,
  onClose,
  onSaved
}: {
  log: FoodLog;
  foodName: string;
  foods: string[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(foodName);
  const [notes, setNotes] = useState(log.notes);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    setSaving(true);
    try {
      await updateFoodLog(log, name, log.eaten_at, notes);
      onSaved();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Modifica non riuscita.");
      setSaving(false);
    }
  };

  return (
    <WellbeingDialogFrame title="Modifica portata" onClose={onClose}>
      <form className="modal-body" onSubmit={save}>
        <label>
          Portata o cibo
          <input autoFocus list={`foods-${log.id}`} value={name} onChange={(event) => setName(event.target.value)} />
        </label>
        <datalist id={`foods-${log.id}`}>
          {foods.map((food) => (
            <option key={food} value={food} />
          ))}
        </datalist>
        <label>
          Nota della portata
          <textarea rows={3} value={notes} onChange={(event) => setNotes(event.target.value)} />
        </label>
        {error && <p className="form-error">{error}</p>}
        <div className="modal-actions">
          <button type="button" onClick={onClose}>
            Annulla
          </button>
          <button className="primary" type="submit" disabled={saving || !name.trim()}>
            <Save size={17} />
            {saving ? "Salvataggio..." : "Salva"}
          </button>
        </div>
      </form>
    </WellbeingDialogFrame>
  );
}

function EditMedicationLogDialog({
  log,
  onClose,
  onSaved
}: {
  log: MedicationLog;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [date, setDate] = useState(formatDateInput(log.date));
  const [kind, setKind] = useState<MedicationKind>(normalizeMedicationKind(log.medication));
  const [doseCount, setDoseCount] = useState(medicationDoseCount(log));
  const [time, setTime] = useState(log.taken_at ? formatTime(log.taken_at) : "08:00");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    setSaving(true);
    try {
      const parsedDate = parseDateInput(date);
      if (!parsedDate) {
        throw new Error("Data non valida. Usa gg/mm/aaaa.");
      }
      await updateMedicationLog(
        log,
        parsedDate,
        kind,
        doseCount,
        doseCount > 0 ? dateTimeFromInputs(date, time) : null
      );
      onSaved();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Modifica non riuscita.");
      setSaving(false);
    }
  };

  return (
    <WellbeingDialogFrame title="Modifica farmaco" onClose={onClose}>
      <form className="modal-body" onSubmit={save}>
        <div className="form-grid">
          <label>
            Farmaco
            <select value={kind} onChange={(event) => setKind(event.target.value as MedicationKind)}>
              {MEDICATION_KINDS.map((option) => (
                <option value={option} key={option}>
                  {MEDICATION_LABELS[option]}
                </option>
              ))}
            </select>
          </label>
          <label>
            Data
            <ItalianDateInput value={date} onChange={setDate} label="Data farmaco" />
          </label>
          {doseCount > 0 && (
            <label>
              Ora
              <input
                inputMode="numeric"
                value={time}
                onChange={(event) => setTime(event.target.value)}
                aria-label="Ora assunzione"
              />
            </label>
          )}
        </div>
        <label>
          Dosi
          <select value={doseCount} onChange={(event) => setDoseCount(Number(event.target.value))}>
            <option value={0}>0 - non preso</option>
            <option value={1}>1 dose</option>
            <option value={2}>2 dosi</option>
          </select>
        </label>
        {error && <p className="form-error">{error}</p>}
        <div className="modal-actions">
          <button type="button" onClick={onClose}>
            Annulla
          </button>
          <button className="primary" type="submit" disabled={saving}>
            <Save size={17} />
            {saving ? "Salvataggio..." : "Salva"}
          </button>
        </div>
      </form>
    </WellbeingDialogFrame>
  );
}

function EditSymptomLogDialog({
  log,
  onClose,
  onSaved
}: {
  log: SymptomLog;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [date, setDate] = useState(formatDateInput(localDateKey(log.occurred_at)));
  const [time, setTime] = useState(formatTime(log.occurred_at));
  const [symptom, setSymptom] = useState<SymptomKind | null>(
    log.symptom ?? (log.vomited ? "episodio_gastrico" : null)
  );
  const [notes, setNotes] = useState(log.notes);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    setSaving(true);
    try {
      await updateSymptomLog(log, {
        occurred_at: dateTimeFromInputs(date, time),
        symptom,
        notes
      });
      onSaved();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Modifica non riuscita.");
      setSaving(false);
    }
  };

  return (
    <WellbeingDialogFrame title="Modifica sintomo" onClose={onClose}>
      <form className="modal-body" onSubmit={save}>
        <div className="form-grid">
          <label>
            Data
            <ItalianDateInput value={date} onChange={setDate} label="Data sintomo" />
          </label>
          <label>
            Ora
            <input
              inputMode="numeric"
              value={time}
              onChange={(event) => setTime(event.target.value)}
              aria-label="Ora sintomo"
            />
          </label>
        </div>
        <label>
          Sintomo
          <select
            value={symptom ?? ""}
            onChange={(event) => setSymptom((event.target.value || null) as SymptomKind | null)}
          >
            <option value="">Malessere generico</option>
            {SYMPTOM_KINDS.map((kind) => (
              <option value={kind} key={kind}>
                {SYMPTOM_LABELS[kind]}
              </option>
            ))}
          </select>
        </label>
        <label>
          Note
          <textarea rows={3} value={notes} onChange={(event) => setNotes(event.target.value)} />
        </label>
        {error && <p className="form-error">{error}</p>}
        <div className="modal-actions">
          <button type="button" onClick={onClose}>
            Annulla
          </button>
          <button className="primary" type="submit" disabled={saving}>
            <Save size={17} />
            {saving ? "Salvataggio..." : "Salva"}
          </button>
        </div>
      </form>
    </WellbeingDialogFrame>
  );
}

function EditVoiceNoteDialog({
  note,
  onClose,
  onSaved
}: {
  note: VoiceNote;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [title, setTitle] = useState(note.title);
  const [notes, setNotes] = useState(note.notes);
  const [transcript, setTranscript] = useState(note.transcript ?? "");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    setSaving(true);
    try {
      await updateVoiceNote(note, { title, notes, transcript });
      onSaved();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Modifica non riuscita.");
      setSaving(false);
    }
  };

  return (
    <WellbeingDialogFrame title="Modifica nota vocale" onClose={onClose}>
      <form className="modal-body" onSubmit={save}>
        <label>
          Titolo
          <input autoFocus value={title} onChange={(event) => setTitle(event.target.value)} />
        </label>
        <label>
          Trascrizione
          <textarea rows={4} value={transcript} onChange={(event) => setTranscript(event.target.value)} />
        </label>
        <label>
          Note
          <textarea rows={3} value={notes} onChange={(event) => setNotes(event.target.value)} />
        </label>
        {error && <p className="form-error">{error}</p>}
        <div className="modal-actions">
          <button type="button" onClick={onClose}>
            Annulla
          </button>
          <button className="primary" type="submit" disabled={saving || !title.trim()}>
            <Save size={17} />
            {saving ? "Salvataggio..." : "Salva"}
          </button>
        </div>
      </form>
    </WellbeingDialogFrame>
  );
}

function HistoryView({ snapshot }: { snapshot: TrackerSnapshot }) {
  const clients = new Map(active(snapshot.clients).map((client) => [client.id, client]));
  const projects = new Map(active(snapshot.projects).map((project) => [project.id, project]));
  const [editing, setEditing] = useState<WorkSession | null>(null);
  // Conteggio virtuale: le ore oltre 50h/settimana scorrono nella settimana
  // successiva solo qui (Storico); fatturazione ed export restano sul consuntivo.
  const carryPlan = useMemo(() => planWeeklyCarryover(snapshot.work_sessions), [snapshot.work_sessions]);

  const cycleCarry = async (session: WorkSession) => {
    const next = session.carry_override === "carry" ? "keep" : session.carry_override === "keep" ? null : "carry";
    await updateWorkSession(session, { carry_override: next });
  };
  const weeks = useMemo(() => {
    const byDate = new Map<string, WorkSession[]>();
    for (const session of active(snapshot.work_sessions).filter((item) => item.ended_at)) {
      const date = localDateKey(session.started_at);
      byDate.set(date, [...(byDate.get(date) ?? []), session]);
    }

    const byWeek = new Map<string, WorkDay[]>();
    for (const sessions of byDate.values()) {
      const day = summarizeWorkDay(sessions);
      if (!day) {
        continue;
      }
      const week = weekStartKey(day.date);
      byWeek.set(week, [...(byWeek.get(week) ?? []), day]);
    }

    return [...byWeek.entries()]
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([week, days]) => ({
        week,
        days: days.sort((a, b) => b.date.localeCompare(a.date)),
        workedMinutes: days.reduce((total, day) => total + day.worked_minutes, 0)
      }));
  }, [snapshot.work_sessions]);

  const remove = async (session: WorkSession) => {
    const client = clients.get(session.client_id)?.name ?? "cliente";
    const project = projects.get(session.project_id)?.name ?? "progetto";
    if (!window.confirm(`Eliminare la sessione ${client} / ${project}?`)) {
      return;
    }
    await deleteWorkSession(session);
  };

  return (
    <main className="wide-content">
      {weeks.length === 0 && (
        <section className="panel empty-state">
          <PanelTitle icon={<Clock size={22} />} title="Storico lavoro" />
          <p>Non ci sono ancora sessioni concluse.</p>
        </section>
      )}
      <WeeklyCarryChart weeks={weeks} carryPlan={carryPlan} />
      {weeks.map((week) => {
        const carry = carryPlan.weeks.get(week.week);
        return (
        <section className="panel history-week" key={week.week}>
          <div className="week-heading">
            <div>
              <span className="eyebrow">Settimana</span>
              <h2>{formatWeekRange(week.week)}</h2>
            </div>
            <div className="week-totals">
              {(carry?.carry_in_minutes ?? 0) > 0 && (
                <span className="carry-chip carry-in" title="ore riportate dal conteggio della settimana precedente">
                  +{formatDuration(carry!.carry_in_minutes)}
                </span>
              )}
              <strong
                className={carry && carry.counted_minutes > carryPlan.cap_minutes ? "over-cap" : undefined}
                title={
                  carry
                    ? `Consuntivo ${formatDuration(carry.actual_minutes)} · in conteggio ${formatDuration(carry.counted_minutes)}`
                    : undefined
                }
              >
                {formatDuration(carry?.counted_minutes ?? week.workedMinutes)}
              </strong>
              {(carry?.carry_out_minutes ?? 0) > 0 && (
                <span className="carry-chip carry-out" title="ore trasferite al conteggio della settimana successiva">
                  {formatDuration(carry!.carry_out_minutes)} →
                </span>
              )}
            </div>
          </div>
          {week.days.map((day) => (
            <WorkDayHistory
              key={day.date}
              day={day}
              clients={clients}
              projects={projects}
              carryPlan={carryPlan}
              onCycleCarry={cycleCarry}
              onEdit={setEditing}
              onDelete={remove}
            />
          ))}
        </section>
        );
      })}
      {editing && (
        <EditWorkSessionDialog
          key={editing.id}
          session={editing}
          snapshot={snapshot}
          onClose={() => setEditing(null)}
        />
      )}
    </main>
  );
}

function WeeklyCarryChart({ weeks, carryPlan }: { weeks: WorkWeek[]; carryPlan: WeeklyCarryPlan }) {
  if (!weeks.length) {
    return null;
  }
  const chronological = [...weeks].reverse();
  const BAR_H = 132;
  const LABEL_H = 24; // label + gap sotto l'area barre
  const barMinutes = (week: WorkWeek) => {
    const carry = carryPlan.weeks.get(week.week);
    return (carry?.carry_in_minutes ?? 0) + week.workedMinutes;
  };
  const scaleMax = Math.max(carryPlan.cap_minutes * 1.15, ...chronological.map(barMinutes));
  const px = (minutes: number) => Math.max(3, Math.round((minutes / scaleMax) * BAR_H));
  return (
    <section className="panel week-chart-panel">
      <div className="section-head">
        <div>
          <span className="eyebrow">Conteggio</span>
          <h2>Settimane (tetto {formatDuration(carryPlan.cap_minutes)})</h2>
        </div>
        <div className="chart-legend">
          <span>
            <i className="seg seg-in" />riportate dentro
          </span>
          <span>
            <i className="seg seg-own" />conteggio qui
          </span>
          <span>
            <i className="seg seg-out" />settimana dopo
          </span>
        </div>
      </div>
      <div className="week-chart">
        <div className="chart-plot" style={{ height: BAR_H + LABEL_H + 30 }}>
          <div className="cap-line" style={{ bottom: LABEL_H + (carryPlan.cap_minutes / scaleMax) * BAR_H }}>
            <span>{formatDuration(carryPlan.cap_minutes)}</span>
          </div>
          {chronological.map((week) => {
            const carry = carryPlan.weeks.get(week.week);
            const carryIn = carry?.carry_in_minutes ?? 0;
            const carryOut = carry?.carry_out_minutes ?? 0;
            const own = Math.max((carry?.counted_minutes ?? week.workedMinutes) - carryIn, 0);
            return (
              <div className="chart-col" key={week.week}>
                <div className="chart-chips">
                  {carryIn > 0 && <span className="carry-chip carry-in row-chip">+{formatDuration(carryIn)}</span>}
                  {carryOut > 0 && <span className="carry-chip carry-out row-chip">{formatDuration(carryOut)} →</span>}
                </div>
                <div
                  className="bar-area"
                  title={`Settimana ${formatWeekRange(week.week)} · consuntivo ${formatDuration(week.workedMinutes)} · in conteggio ${formatDuration(carry?.counted_minutes ?? week.workedMinutes)}`}
                >
                  {carryOut > 0 && <span className="seg seg-out" style={{ height: px(carryOut) }} />}
                  {own > 0 && <span className="seg seg-own" style={{ height: px(own) }} />}
                  {carryIn > 0 && <span className="seg seg-in" style={{ height: px(carryIn) }} />}
                </div>
                <span className="chart-label">{formatWeekRange(week.week)}</span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function WorkDayHistory({
  day,
  clients,
  projects,
  carryPlan,
  onCycleCarry,
  onEdit,
  onDelete
}: {
  day: WorkDay;
  clients: Map<string, Client>;
  projects: Map<string, Project>;
  carryPlan: WeeklyCarryPlan;
  onCycleCarry: (session: WorkSession) => void;
  onEdit: (session: WorkSession) => void;
  onDelete: (session: WorkSession) => void;
}) {
  return (
    <section className="history-day">
      <div className="day-heading">
        <div>
          <h3>{formatLongDate(day.date)}</h3>
          <span>
            Copertura {formatTime(day.first_started_at)}–{formatTime(day.last_ended_at)}
          </span>
        </div>
        <div className="day-totals">
          <span>
            Lavoro <strong>{formatDuration(day.worked_minutes)}</strong>
          </span>
          <span>
            Pause <strong>{formatDuration(day.pause_minutes)}</strong>
          </span>
          {day.overlap_minutes > 0 && (
            <span className="overlap-warning">
              <AlertTriangle size={15} />
              Sovrapposte {formatDuration(day.overlap_minutes)}
            </span>
          )}
        </div>
      </div>
      <WorkDayTimeline day={day} projects={projects} />
      {day.gaps.length > 0 && (
        <div className="gap-list">
          <span>Pause:</span>
          {day.gaps.map((gap) => (
            <span key={`${gap.started_at}-${gap.ended_at}`}>
              {formatTime(gap.started_at)}–{formatTime(gap.ended_at)} ({formatDuration(gap.minutes)})
            </span>
          ))}
        </div>
      )}
      <div className="table-wrap">
        <table className="history-table">
          <thead>
            <tr>
              <th>Inizio</th>
              <th>Fine</th>
              <th>Cliente</th>
              <th>Progetto</th>
              <th>Durata</th>
              <th>Note</th>
              <th>
                <span className="sr-only">Azioni</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {day.sessions
              .slice()
              .sort((a, b) => b.started_at.localeCompare(a.started_at))
              .map((session) => {
              const clientName = clients.get(session.client_id)?.name ?? "-";
              const projectName = projects.get(session.project_id)?.name ?? "-";
              const durationMinutes = minutesBetween(session.started_at, session.ended_at ?? session.started_at);
              const carriedMinutes = carryPlan.sessions.get(session.id)?.carried_minutes ?? 0;
              const override = session.carry_override ?? null;
              const carryTitle =
                override === "carry"
                  ? "Riga spostata nel conteggio della settimana dopo — clicca per àncorarla a questa settimana"
                  : override === "keep"
                    ? "Riga àncorata a questa settimana — clicca per tornare al riporto automatico"
                    : carriedMinutes > 0
                      ? `Riporto automatico: ${formatDuration(carriedMinutes)} nel conteggio della settimana dopo — clicca per spostare qui solo il necessario o tutta la riga`
                      : "Riporto automatico entro il tetto di 50h — clicca per spostare l'intera riga nella settimana dopo";
              const carryClass =
                override === "carry"
                  ? " carry-active"
                  : override === "keep"
                    ? " keep-active"
                    : carriedMinutes > 0
                      ? " over-active"
                      : "";
              return (
                <tr key={session.id}>
                  <td data-label="Inizio">
                    <InlineValueEditor
                      displayValue={formatTime(session.started_at)}
                      editValue={formatTime(session.started_at)}
                      label={`Modifica orario di inizio ${projectName}`}
                      hint="730 → 07:30"
                      onCommit={(value) => updateWorkSessionStartClock(session, value)}
                    />
                  </td>
                  <td data-label="Fine">
                    <InlineValueEditor
                      displayValue={formatTime(session.ended_at ?? session.started_at)}
                      editValue={formatTime(session.ended_at ?? session.started_at)}
                      label={`Modifica orario di fine ${projectName}`}
                      hint="1850 → 18:50"
                      onCommit={(value) => updateWorkSessionEndClock(session, value)}
                    />
                  </td>
                  <td data-label="Cliente">{clientName}</td>
                  <td data-label="Progetto">{projectName}</td>
                  <td data-label="Durata">
                    <div className="duration-cell">
                      <InlineValueEditor
                        displayValue={formatDuration(durationMinutes)}
                        editValue={formatDurationEditorValue(durationMinutes)}
                        label={`Modifica durata ${projectName}`}
                        hint="130 → 1h 30m"
                        onCommit={(value) => updateWorkSessionDuration(session, value)}
                      />
                      {carriedMinutes > 0 && (
                        <span className="carry-chip row-chip carry-out" title="trasferite nel conteggio della settimana dopo">
                          −{formatDuration(carriedMinutes)} →
                        </span>
                      )}
                    </div>
                  </td>
                  <td data-label="Note">{session.notes || "-"}</td>
                  <td data-label="Azioni">
                    <div className="row-actions">
                      <button
                        className={`icon-button small carry-toggle${carryClass}`}
                        type="button"
                        onClick={() => onCycleCarry(session)}
                        title={carryTitle}
                        aria-label={`Riporto conteggio ${projectName}`}
                      >
                        {override === "carry" ? (
                          <ArrowRightCircle size={16} />
                        ) : override === "keep" ? (
                          <Anchor size={16} />
                        ) : (
                          <CornerDownRight size={16} />
                        )}
                      </button>
                      <button
                        className="icon-button small"
                        type="button"
                        onClick={() => onEdit(session)}
                        title="Modifica dettagli"
                        aria-label={`Modifica dettagli ${clientName} ${projectName}`}
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        className="icon-button small danger-outline"
                        type="button"
                        onClick={() => onDelete(session)}
                        title="Elimina sessione"
                        aria-label={`Elimina sessione ${clientName} ${projectName}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
              })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function InlineValueEditor({
  displayValue,
  editValue,
  label,
  hint,
  onCommit
}: {
  displayValue: string;
  editValue: string;
  label: string;
  hint: string;
  onCommit: (value: string) => Promise<unknown>;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(editValue);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const savingRef = useRef(false);
  const cancelRequestedRef = useRef(false);

  useEffect(() => {
    if (!editing) {
      return;
    }
    inputRef.current?.focus();
    inputRef.current?.select();
  }, [editing]);

  useEffect(() => {
    if (editing && !dirty) {
      setValue(editValue);
    }
  }, [dirty, editing, editValue]);

  // Se il valore ricalcolato cambia mentre si modifica (es. fine dipendente da inizio)
  // e l'utente non ha ancora scritto, il testo va riselezionato: senza, la digitazione
  // verrebbe aggiunta in coda al valore automatico.
  useEffect(() => {
    if (editing && !dirty) {
      inputRef.current?.select();
    }
  }, [editing, dirty, value]);

  const open = () => {
    cancelRequestedRef.current = false;
    setValue(editValue);
    setDirty(false);
    setError("");
    setEditing(true);
  };

  const cancel = () => {
    cancelRequestedRef.current = true;
    setEditing(false);
    setError("");
    setValue(editValue);
  };

  const commit = async () => {
    if (savingRef.current || cancelRequestedRef.current) {
      return;
    }
    if (!dirty) {
      setEditing(false);
      return;
    }
    savingRef.current = true;
    setSaving(true);
    setError("");
    try {
      await onCommit(value);
      setEditing(false);
    } catch (commitError) {
      setError(commitError instanceof Error ? commitError.message : "Valore non valido.");
      window.requestAnimationFrame(() => inputRef.current?.focus());
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <button
        className="inline-edit-button"
        type="button"
        onClick={open}
        onFocus={open}
        title={`${label}. ${hint}`}
        aria-label={label}
      >
        <span>{displayValue}</span>
        <Pencil size={12} aria-hidden="true" />
      </button>
    );
  }

  return (
    <span className="inline-editor">
      <input
        ref={inputRef}
        className={error ? "invalid" : ""}
        type="text"
        inputMode="numeric"
        enterKeyHint="done"
        autoComplete="off"
        value={value}
        aria-busy={saving}
        aria-label={label}
        aria-invalid={Boolean(error)}
        onChange={(event) => {
          setDirty(true);
          setValue(event.target.value);
        }}
        onBlur={() => void commit()}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            void commit();
          }
          if (event.key === "Escape") {
            event.preventDefault();
            cancel();
          }
        }}
      />
      {error && (
        <span className="inline-edit-error" role="alert">
          {error}
        </span>
      )}
    </span>
  );
}

function WorkDayTimeline({ day, projects }: { day: WorkDay; projects: Map<string, Project> }) {
  const start = new Date(day.first_started_at).getTime();
  const end = new Date(day.last_ended_at).getTime();
  const span = Math.max(1, end - start);

  return (
    <div className="timeline">
      <div className="timeline-track" aria-label={`Copertura giornaliera da ${formatTime(day.first_started_at)} a ${formatTime(day.last_ended_at)}`}>
        {day.sessions.map((session) => {
          const sessionStart = new Date(session.started_at).getTime();
          const sessionEnd = new Date(session.ended_at ?? session.started_at).getTime();
          const left = ((sessionStart - start) / span) * 100;
          const width = ((sessionEnd - sessionStart) / span) * 100;
          return (
            <span
              className="timeline-session"
              key={session.id}
              style={{ left: `${left}%`, width: `${Math.max(0.5, width)}%` }}
              title={`${formatTime(session.started_at)}–${formatTime(session.ended_at ?? session.started_at)} · ${
                projects.get(session.project_id)?.name ?? "Progetto"
              }`}
            />
          );
        })}
      </div>
      <div className="timeline-labels">
        <span>{formatTime(day.first_started_at)}</span>
        <span>{formatTime(day.last_ended_at)}</span>
      </div>
    </div>
  );
}

function EditWorkSessionDialog({
  session,
  snapshot,
  onClose
}: {
  session: WorkSession;
  snapshot: TrackerSnapshot;
  onClose: () => void;
}) {
  const clients = active(snapshot.clients);
  const projects = active(snapshot.projects);
  const initialClient = clients.find((client) => client.id === session.client_id);
  const initialProject = projects.find((project) => project.id === session.project_id);
  const [clientName, setClientName] = useState(initialClient?.name ?? "");
  const [projectName, setProjectName] = useState(initialProject?.name ?? "");
  const [sessionDate, setSessionDate] = useState(formatDateInput(localDateKey(session.started_at)));
  const [notes, setNotes] = useState(session.notes);
  const [billable, setBillable] = useState(session.billable);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const selectedClient = clients.find((client) => client.name.toLowerCase() === clientName.trim().toLowerCase());
  const availableProjects = selectedClient
    ? projects.filter((project) => project.client_id === selectedClient.id && !project.archived)
    : projects;

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose]);

  const save = async () => {
    if (!clientName.trim() || !projectName.trim()) {
      setError("Cliente e progetto sono obbligatori.");
      return;
    }
    const parsedDate = parseDateInput(sessionDate);
    if (!parsedDate) {
      setError("Data non valida. Usa gg/mm/aaaa.");
      return;
    }
    const dateParts = parsedDate.split("-").map(Number);
    const start = new Date(session.started_at);
    const durationMilliseconds = Math.max(
      60_000,
      new Date(session.ended_at ?? session.started_at).getTime() - start.getTime()
    );
    start.setFullYear(dateParts[0], dateParts[1] - 1, dateParts[2]);
    const end = new Date(start.getTime() + durationMilliseconds);

    setSaving(true);
    setError("");
    try {
      const client = await ensureClient(clientName);
      const project = await ensureProject(client.id, projectName);
      await updateWorkSession(session, {
        client_id: client.id,
        project_id: project.id,
        started_at: start.toISOString(),
        ended_at: end.toISOString(),
        notes: notes.trim(),
        billable
      });
      onClose();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "Salvataggio non riuscito.");
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-session-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="modal-heading">
          <div>
            <span className="eyebrow">Storico lavoro</span>
            <h2 id="edit-session-title">Dettagli sessione</h2>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Chiudi modifica">
            <X size={20} />
          </button>
        </div>
        <div className="modal-body">
          <div className="form-grid">
            <label>
              Cliente
              <input
                list={`edit-clients-${session.id}`}
                value={clientName}
                onChange={(event) => setClientName(event.target.value)}
              />
            </label>
            <label>
              Progetto
              <input
                list={`edit-projects-${session.id}`}
                value={projectName}
                onChange={(event) => setProjectName(event.target.value)}
              />
            </label>
            <label>
              Data
              <ItalianDateInput
                value={sessionDate}
                onChange={setSessionDate}
                label="Data"
              />
            </label>
          </div>
          <datalist id={`edit-clients-${session.id}`}>
            {clients.map((client) => (
              <option key={client.id} value={client.name} />
            ))}
          </datalist>
          <datalist id={`edit-projects-${session.id}`}>
            {availableProjects.map((project) => (
              <option key={project.id} value={project.name} />
            ))}
          </datalist>
          <label>
            Note
            <textarea rows={3} value={notes} onChange={(event) => setNotes(event.target.value)} />
          </label>
          <label className="checkbox-label">
            <input type="checkbox" checked={billable} onChange={(event) => setBillable(event.target.checked)} />
            Fatturabile
          </label>
          {error && <p className="form-error">{error}</p>}
        </div>
        <div className="button-row modal-actions">
          <button type="button" onClick={onClose}>
            Annulla
          </button>
          <button className="primary" type="button" onClick={save} disabled={saving}>
            <Save size={18} />
            {saving ? "Salvataggio..." : "Salva"}
          </button>
        </div>
      </section>
    </div>
  );
}

function InvoiceView({ snapshot }: { snapshot: TrackerSnapshot }) {
  const clients = active(snapshot.clients);
  const projects = new Map(snapshot.projects.map((project) => [project.id, project]));
  const [fromDate, setFromDate] = useState(monthStart());
  const [toDate, setToDate] = useState(todayKey());
  const [clientId, setClientId] = useState("");

  const summaries = useMemo(
    () => buildInvoiceSummaries(clients, snapshot.work_sessions, fromDate, toDate, clientId),
    [clients, snapshot.work_sessions, fromDate, toDate, clientId]
  );
  const totalAmount = summaries.reduce((sum, item) => sum + item.amount_cents, 0);
  const totalMinutes = summaries.reduce((sum, item) => sum + item.billable_minutes, 0);

  const copySummary = async () => {
    const text = summaries
      .map((summary) => `${summary.client.name}: ${formatDuration(summary.billable_minutes)} · ${formatMoney(summary.amount_cents)}`)
      .join("\n");
    await navigator.clipboard.writeText(text);
  };

  const downloadCsv = () => {
    const csv = invoiceToCsv(summaries, (projectId) => projects.get(projectId)?.name ?? "");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `pre-fattura-${fromDate}-${toDate}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <main className="wide-content">
      <section className="panel">
        <PanelTitle icon={<FileText size={22} />} title="Pre-fattura" />
        <div className="form-grid invoice-filters">
          <label>
            Da
            <input type="date" value={fromDate} onChange={(event) => setFromDate(event.target.value)} />
          </label>
          <label>
            A
            <input type="date" value={toDate} onChange={(event) => setToDate(event.target.value)} />
          </label>
          <label>
            Cliente
            <select value={clientId} onChange={(event) => setClientId(event.target.value)}>
              <option value="">Tutti</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="summary-strip">
          <span>{formatDuration(totalMinutes)}</span>
          <strong>{formatMoney(totalAmount)}</strong>
        </div>
        <div className="button-row">
          <button type="button" onClick={copySummary}>
            <Save size={18} />
            Copia
          </button>
          <button type="button" onClick={downloadCsv}>
            <Download size={18} />
            CSV
          </button>
        </div>
      </section>
      {summaries.map((summary) => (
        <section className="panel" key={summary.client.id}>
          <h2>{summary.client.name}</h2>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Progetto</th>
                  <th>Esatto</th>
                  <th>Fatt.</th>
                  <th>Importo</th>
                </tr>
              </thead>
              <tbody>
                {summary.lines.map((line) => (
                  <tr key={line.session.id}>
                    <td>{formatDateTime(line.session.started_at)}</td>
                    <td>{projects.get(line.session.project_id)?.name ?? "-"}</td>
                    <td>{formatDuration(line.exact_minutes)}</td>
                    <td>{formatDuration(line.billable_minutes)}</td>
                    <td>{formatMoney(line.amount_cents, summary.client.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ))}
    </main>
  );
}

function PwaStatusPanel({
  worker,
  installed,
  installAvailable,
  onInstall
}: {
  worker: PwaWorkerState;
  installed: boolean;
  installAvailable: boolean;
  onInstall: () => Promise<void>;
}) {
  const ready = worker.stage === "ready";
  const failed = worker.stage === "error" || worker.stage === "unsupported";
  const status = installed
    ? "Installata come app"
    : ready
      ? "Offline pronto"
      : failed
        ? "Offline non disponibile"
        : "Preparazione offline";
  const ios = isIosDevice();

  return (
    <section className="panel">
      <PanelTitle icon={<Download size={22} />} title="App e offline" />
      <div className="backend-summary">
        <span className={`backend-auth ${ready || installed ? "authenticated" : "required"}`}>
          {ready || installed ? <Check size={16} /> : <AlertTriangle size={16} />}
          {status}
        </span>
        <dl className="sync-diagnostics pwa-diagnostics">
          <div>
            <dt>Apertura</dt>
            <dd>{installed ? "App" : "Browser"}</dd>
          </div>
          <div>
            <dt>Service worker</dt>
            <dd>{ready ? "Attivo" : worker.stage === "registering" ? "Avvio..." : "Non attivo"}</dd>
          </div>
          <div>
            <dt>Cache</dt>
            <dd>{ready ? "Pronta" : "Non pronta"}</dd>
          </div>
        </dl>
      </div>

      {installAvailable && !installed && (
        <button className="primary" type="button" onClick={onInstall}>
          <Download size={18} />
          Installa app
        </button>
      )}
      {!installAvailable && ready && !installed && (
        <p className="muted">
          {ios
            ? "In Safari usa Condividi e poi Aggiungi alla schermata Home."
            : "Nel menu del browser usa Installa app. Un semplice collegamento continua ad aprire il browser."}
        </p>
      )}
      {failed && (
        <>
          <p className="form-error">{pwaWorkerErrorMessage(worker)}</p>
          <a className="button-link" href={caCertificateUrl()}>
            <Download size={18} />
            Scarica certificato HTTPS
          </a>
        </>
      )}
    </section>
  );
}

function SettingsView({
  snapshot,
  authenticated,
  diagnostics,
  onAuthChange,
  onSyncMessage,
  onSync,
  pwaWorker,
  installed,
  installAvailable,
  onInstall
}: {
  snapshot: TrackerSnapshot;
  authenticated: boolean;
  diagnostics: SyncDiagnostics;
  onAuthChange: (value: boolean) => void;
  onSyncMessage: (value: string) => void;
  onSync: () => Promise<void>;
  pwaWorker: PwaWorkerState;
  installed: boolean;
  installAvailable: boolean;
  onInstall: () => Promise<void>;
}) {
  const [password, setPassword] = useState("");
  const [backendError, setBackendError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [clientName, setClientName] = useState("");
  const [projectName, setProjectName] = useState("");
  const [clientToRename, setClientToRename] = useState<Client | null>(null);
  const [projectToRename, setProjectToRename] = useState<Project | null>(null);
  const [clientToMerge, setClientToMerge] = useState<Client | null>(null);
  const [maintenanceMessage, setMaintenanceMessage] = useState("");
  const clients = active(snapshot.clients)
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name));
  const projects = active(snapshot.projects).filter((project) => !project.archived);

  const doLogin = async () => {
    if (!password) {
      setBackendError("Inserisci la password backend.");
      return;
    }
    setBackendError("");
    setLoggingIn(true);
    try {
      await login(password);
      setPassword("");
      onAuthChange(true);
      onSyncMessage("Accesso eseguito. Sync in corso...");
      await onSync();
    } catch (error) {
      setBackendError(syncErrorMessage(error));
    } finally {
      setLoggingIn(false);
    }
  };

  const doLogout = async () => {
    setBackendError("");
    try {
      await logout();
      onAuthChange(false);
      onSyncMessage("Accesso richiesto");
    } catch (error) {
      setBackendError(syncErrorMessage(error));
    }
  };

  const addClientProject = async () => {
    if (!clientName.trim()) {
      return;
    }
    const client = await ensureClient(clientName);
    if (projectName.trim()) {
      await ensureProject(client.id, projectName);
    }
    setClientName("");
    setProjectName("");
  };

  const removeClient = async (client: Client) => {
    if (!window.confirm(`Eliminare il cliente "${client.name}" e i suoi dati non utilizzati?`)) {
      return;
    }
    setMaintenanceMessage("");
    try {
      await deleteClient(client);
      setMaintenanceMessage(`Cliente "${client.name}" eliminato.`);
    } catch (error) {
      setMaintenanceMessage(error instanceof Error ? error.message : "Eliminazione non riuscita.");
    }
  };

  const removeProject = async (project: Project) => {
    if (!window.confirm(`Eliminare il progetto "${project.name}"?`)) {
      return;
    }
    setMaintenanceMessage("");
    try {
      await deleteProject(project);
      setMaintenanceMessage(`Progetto "${project.name}" eliminato.`);
    } catch (error) {
      setMaintenanceMessage(error instanceof Error ? error.message : "Eliminazione non riuscita.");
    }
  };

  return (
    <main className="wide-content">
      <PwaStatusPanel
        worker={pwaWorker}
        installed={installed}
        installAvailable={installAvailable}
        onInstall={onInstall}
      />
      <section className="panel">
        <PanelTitle icon={<LogIn size={22} />} title="Backend" />
        <div className="backend-summary">
          <span className={`backend-auth ${authenticated ? "authenticated" : "required"}`}>
            {authenticated ? <Check size={16} /> : <AlertTriangle size={16} />}
            {authenticated ? "Accesso attivo" : "Accesso richiesto"}
          </span>
          <dl className="sync-diagnostics">
            <div>
              <dt>In coda</dt>
              <dd>{diagnostics.pending}</dd>
            </div>
            <div>
              <dt>Revisione</dt>
              <dd>{diagnostics.latestRev}</dd>
            </div>
            <div>
              <dt>Ultimo sync</dt>
              <dd>{formatSyncDate(diagnostics.lastSyncAt)}</dd>
            </div>
          </dl>
        </div>
        <div className="input-with-button">
          <input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            placeholder={authenticated ? "Sessione attiva" : "Password backend"}
          />
          {authenticated ? (
            <button className="icon-button" type="button" onClick={doLogout} title="Logout">
              <LogOut size={19} />
            </button>
          ) : (
            <button className="icon-button" type="button" onClick={doLogin} title="Login" disabled={loggingIn}>
              <LogIn size={19} />
            </button>
          )}
        </div>
        {backendError && <p className="form-error">{backendError}</p>}
        {backendError.includes("certificato HTTPS") && (
          <a className="button-link" href={caCertificateUrl()}>
            <Download size={18} />
            Scarica certificato HTTPS
          </a>
        )}
        {!backendError && diagnostics.lastError && (
          <p className="form-error">Ultimo errore sync: {diagnostics.lastError}</p>
        )}
        {authenticated && (
          <button type="button" onClick={onSync}>
            <RefreshCw size={17} />
            Riprova ora
          </button>
        )}
      </section>
      <section className="panel">
        <PanelTitle icon={<Plus size={22} />} title="Clienti e progetti" />
        <div className="form-grid">
          <label>
            Cliente
            <input value={clientName} onChange={(event) => setClientName(event.target.value)} />
          </label>
          <label>
            Progetto
            <input value={projectName} onChange={(event) => setProjectName(event.target.value)} />
          </label>
        </div>
        <button type="button" onClick={addClientProject}>
          <Plus size={18} />
          Aggiungi
        </button>
        {maintenanceMessage && (
          <p className={maintenanceMessage.includes("non ") ? "form-error" : "muted"}>{maintenanceMessage}</p>
        )}
        <div className="client-project-list">
          {clients.length === 0 && <p className="muted">Nessun cliente inserito.</p>}
          {clients.map((client) => {
            const clientProjects = projects
              .filter((project) => project.client_id === client.id)
              .sort((a, b) => a.name.localeCompare(b.name));
            return (
              <section className="client-project-group" key={client.id}>
                <div className="client-project-heading">
                  <div>
                    <h3>{client.name}</h3>
                    <span>
                      {clientProjects.length} {clientProjects.length === 1 ? "progetto" : "progetti"}
                    </span>
                  </div>
                  <div className="entity-actions">
                    <button
                      className="icon-button small"
                      type="button"
                      onClick={() => setClientToRename(client)}
                      title={`Modifica cliente ${client.name}`}
                      aria-label={`Modifica cliente ${client.name}`}
                    >
                      <Pencil size={16} />
                    </button>
                    {clients.length > 1 && (
                      <button
                        className="icon-button small"
                        type="button"
                        onClick={() => setClientToMerge(client)}
                        title={`Unisci cliente ${client.name}`}
                        aria-label={`Unisci cliente ${client.name}`}
                      >
                        <MergeIcon size={16} />
                      </button>
                    )}
                    <button
                      className="icon-button small danger-outline"
                      type="button"
                      onClick={() => removeClient(client)}
                      title={`Elimina cliente ${client.name}`}
                      aria-label={`Elimina cliente ${client.name}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                <div className="project-tags">
                  {clientProjects.map((project) => (
                    <div className="project-tag" key={project.id}>
                      <span>{project.name}</span>
                      <button
                        type="button"
                        onClick={() => setProjectToRename(project)}
                        title={`Modifica progetto ${project.name}`}
                        aria-label={`Modifica progetto ${project.name}`}
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        className="project-delete"
                        type="button"
                        onClick={() => removeProject(project)}
                        title={`Elimina progetto ${project.name}`}
                        aria-label={`Elimina progetto ${project.name}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                  {clientProjects.length === 0 && <span className="muted">Nessun progetto</span>}
                </div>
              </section>
            );
          })}
        </div>
      </section>
      <section className="panel">
        <PanelTitle icon={<FileText size={22} />} title="Tariffe per decorrenza" />
        <div className="rate-client-list">
          {clients.length === 0 && <p className="muted">Aggiungi prima un cliente.</p>}
          {clients.map((client) => (
            <ClientRateEditor
              key={client.id}
              client={client}
              rates={active(snapshot.client_rates).filter((rate) => rate.client_id === client.id)}
            />
          ))}
        </div>
      </section>
      {clientToRename && (
        <RenameEntityDialog
          title="Modifica cliente"
          label="Nome cliente"
          initialName={clientToRename.name}
          onClose={() => setClientToRename(null)}
          onSave={async (name) => {
            await renameClient(clientToRename, name);
            setClientToRename(null);
            setMaintenanceMessage(`Cliente rinominato in "${name.trim()}".`);
          }}
        />
      )}
      {projectToRename && (
        <RenameEntityDialog
          title="Modifica progetto"
          label="Nome progetto"
          initialName={projectToRename.name}
          onClose={() => setProjectToRename(null)}
          onSave={async (name) => {
            await renameProject(projectToRename, name);
            setProjectToRename(null);
            setMaintenanceMessage(`Progetto rinominato in "${name.trim()}".`);
          }}
        />
      )}
      {clientToMerge && (
        <MergeClientDialog
          source={clientToMerge}
          clients={clients}
          onClose={() => setClientToMerge(null)}
          onMerged={(message) => {
            setClientToMerge(null);
            setMaintenanceMessage(message);
          }}
        />
      )}
    </main>
  );
}

function RenameEntityDialog({
  title,
  label,
  initialName,
  onClose,
  onSave
}: {
  title: string;
  label: string;
  initialName: string;
  onClose: () => void;
  onSave: (name: string) => Promise<void>;
}) {
  const [name, setName] = useState(initialName);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    setSaving(true);
    try {
      await onSave(name);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Modifica non riuscita.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <section className="modal compact-modal" role="dialog" aria-modal="true" aria-label={title}>
        <div className="modal-heading">
          <h2>{title}</h2>
          <button className="icon-button" type="button" onClick={onClose} title="Chiudi">
            <X size={20} />
          </button>
        </div>
        <form className="modal-body" onSubmit={save}>
          <label>
            {label}
            <input autoFocus value={name} onChange={(event) => setName(event.target.value)} />
          </label>
          {error && <p className="form-error">{error}</p>}
          <div className="modal-actions">
            <button type="button" onClick={onClose}>
              Annulla
            </button>
            <button className="primary" type="submit" disabled={saving || !name.trim()}>
              <Save size={17} />
              {saving ? "Salvataggio..." : "Salva"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

function MergeClientDialog({
  source,
  clients,
  onClose,
  onMerged
}: {
  source: Client;
  clients: Client[];
  onClose: () => void;
  onMerged: (message: string) => void;
}) {
  const targets = clients.filter((client) => client.id !== source.id);
  const [targetId, setTargetId] = useState(targets[0]?.id ?? "");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const target = targets.find((client) => client.id === targetId);

  const merge = async () => {
    if (!target) {
      setError("Scegli il cliente da mantenere.");
      return;
    }
    setError("");
    setSaving(true);
    try {
      const result = await mergeClients(source, target);
      onMerged(
        `"${source.name}" unito a "${target.name}": ${result.sessions} ${
          result.sessions === 1 ? "sessione spostata" : "sessioni spostate"
        }.`
      );
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Unione non riuscita.");
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <section className="modal compact-modal" role="dialog" aria-modal="true" aria-label="Unisci cliente">
        <div className="modal-heading">
          <div>
            <h2>Unisci cliente</h2>
            <p className="muted">Sposta tutti i dati di “{source.name}” nel cliente scelto.</p>
          </div>
          <button className="icon-button" type="button" onClick={onClose} title="Chiudi">
            <X size={20} />
          </button>
        </div>
        <div className="modal-body">
          <label>
            Cliente da mantenere
            <select value={targetId} onChange={(event) => setTargetId(event.target.value)}>
              {targets.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name} ·{" "}
                  {client.hourly_rate_cents
                    ? `${formatMoney(client.hourly_rate_cents, client.currency)}/ora`
                    : "tariffa non impostata"}
                </option>
              ))}
            </select>
          </label>
          <p className="merge-warning">
            Il cliente “{source.name}” verra eliminato dopo aver spostato sessioni, progetti, tariffe e luoghi.
          </p>
          {error && <p className="form-error">{error}</p>}
          <div className="modal-actions">
            <button type="button" onClick={onClose}>
              Annulla
            </button>
            <button className="primary" type="button" onClick={merge} disabled={saving || !target}>
              <MergeIcon size={17} />
              {saving ? "Unione..." : "Unisci"}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

function ClientRateEditor({ client, rates }: { client: Client; rates: ClientRate[] }) {
  const currentRate = effectiveClientRate(rates, client.id, todayKey());
  const initialRate = currentRate?.hourly_rate_cents ?? client.hourly_rate_cents;
  const [rate, setRate] = useState(String((initialRate / 100).toFixed(2)));
  const [validFrom, setValidFrom] = useState(formatDateInput(currentQuarterStart()));
  const [increment, setIncrement] = useState(String(client.billing_increment_minutes || 15));
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const sortedRates = rates.slice().sort((a, b) => b.valid_from.localeCompare(a.valid_from));

  useEffect(() => {
    setRate(String((initialRate / 100).toFixed(2)));
    setIncrement(String(client.billing_increment_minutes || 15));
  }, [client.billing_increment_minutes, initialRate]);

  const save = async () => {
    const parsedRate = Number(rate.replace(",", "."));
    const parsedIncrement = Number(increment);
    const parsedValidFrom = parseDateInput(validFrom);
    if (!parsedValidFrom) {
      setMessage("Data non valida. Usa gg/mm/aaaa.");
      return;
    }
    setSaving(true);
    setMessage("");
    try {
      await saveClientRate(client, parsedRate, parsedValidFrom);
      await updateClientBillingIncrement(client.id, parsedIncrement);
      setMessage("Tariffa salvata.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Salvataggio non riuscito.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item: ClientRate) => {
    if (!window.confirm(`Eliminare la tariffa con decorrenza ${item.valid_from}?`)) {
      return;
    }
    await deleteClientRate(item);
    setMessage("Tariffa eliminata.");
  };

  const removeLegacy = async () => {
    if (!window.confirm("Eliminare la tariffa iniziale senza decorrenza?")) {
      return;
    }
    await clearClientLegacyRate(client);
    setMessage("Tariffa iniziale eliminata.");
  };

  const reprice = async () => {
    if (!window.confirm("Ricalcolare le sessioni concluse usando questo storico tariffe?")) {
      return;
    }
    const count = await repriceClientWorkSessions(client.id);
    setMessage(`${count} ${count === 1 ? "sessione aggiornata" : "sessioni aggiornate"}.`);
  };

  return (
    <section className="rate-client">
      <div className="rate-client-heading">
        <div>
          <h3>{client.name}</h3>
          <span>
            {currentRate
              ? `${formatMoney(currentRate.hourly_rate_cents, currentRate.currency)}/ora dal ${formatDateKey(
                  currentRate.valid_from
                )}`
              : initialRate
                ? `${formatMoney(initialRate, client.currency)}/ora`
                : "Tariffa non impostata"}
          </span>
        </div>
        {(sortedRates.length > 0 || client.hourly_rate_cents > 0) && (
          <button type="button" onClick={reprice}>
            <RefreshCw size={17} />
            Ricalcola storico
          </button>
        )}
      </div>
      <div className="rate-editor-grid">
        <label>
          Euro/ora
          <input
            inputMode="decimal"
            value={rate}
            onChange={(event) => setRate(event.target.value)}
            aria-label={`Euro/ora ${client.name}`}
          />
        </label>
        <label>
          Decorrenza
          <ItalianDateInput
            value={validFrom}
            onChange={setValidFrom}
            label={`Decorrenza ${client.name}`}
          />
        </label>
        <label>
          Scatto min.
          <input
            type="number"
            min="1"
            step="1"
            value={increment}
            onChange={(event) => setIncrement(event.target.value)}
            aria-label={`Scatto minimo ${client.name}`}
          />
        </label>
        <button className="primary rate-save" type="button" onClick={save} disabled={saving}>
          <Save size={18} />
          {saving ? "Salvataggio..." : "Salva"}
        </button>
      </div>
      {message && <p className={message.includes("non ") || message.includes("non valida") ? "form-error" : "muted"}>{message}</p>}
      {(sortedRates.length > 0 || client.hourly_rate_cents > 0) && (
        <div className="rate-history" aria-label={`Storico tariffe ${client.name}`}>
          {sortedRates.map((item) => (
            <div key={item.id}>
              <span>{formatDateKey(item.valid_from)}</span>
              <strong>{formatMoney(item.hourly_rate_cents, item.currency)}/ora</strong>
              <button
                className="icon-button danger-outline"
                type="button"
                onClick={() => remove(item)}
                title={`Elimina tariffa ${formatDateKey(item.valid_from)}`}
                aria-label={`Elimina tariffa ${formatDateKey(item.valid_from)} di ${client.name}`}
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
          {client.hourly_rate_cents > 0 && (
            <div className="legacy-rate">
              <span>Iniziale, senza data</span>
              <strong>{formatMoney(client.hourly_rate_cents, client.currency)}/ora</strong>
              <button
                className="icon-button danger-outline"
                type="button"
                onClick={removeLegacy}
                title={`Elimina tariffa iniziale di ${client.name}`}
                aria-label={`Elimina tariffa iniziale di ${client.name}`}
              >
                <Trash2 size={16} />
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function PanelTitle({ icon, title }: { icon: React.ReactNode; title: string }) {
  return (
    <div className="panel-title">
      {icon}
      <h2>{title}</h2>
    </div>
  );
}

function ItalianDateInput({
  value,
  onChange,
  label
}: {
  value: string;
  onChange: (value: string) => void;
  label: string;
}) {
  const pickerRef = useRef<HTMLInputElement | null>(null);
  const nativeValue = parseDateInput(value) ?? "";

  const openCalendar = () => {
    const picker = pickerRef.current;
    if (!picker) {
      return;
    }
    try {
      picker.showPicker();
    } catch {
      picker.click();
    }
  };

  return (
    <span className="date-input">
      <input
        type="text"
        inputMode="numeric"
        placeholder="gg/mm/aaaa"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        aria-label={label}
      />
      <input
        ref={pickerRef}
        className="native-date-picker"
        type="date"
        value={nativeValue}
        onChange={(event) => {
          if (event.target.value) {
            onChange(formatDateInput(event.target.value));
          }
        }}
        aria-label={`Calendario ${label.toLowerCase()}`}
        tabIndex={-1}
      />
      <button
        className="icon-button date-picker-button"
        type="button"
        onClick={openCalendar}
        title={`Apri calendario ${label.toLowerCase()}`}
        aria-label={`Apri calendario ${label.toLowerCase()}`}
      >
        <CalendarDays size={18} />
      </button>
    </span>
  );
}

function monthStart(): string {
  const now = new Date();
  return todayKey(new Date(now.getFullYear(), now.getMonth(), 1));
}

function currentQuarterStart(): string {
  const now = new Date();
  const firstMonth = Math.floor(now.getMonth() / 3) * 3;
  return todayKey(new Date(now.getFullYear(), firstMonth, 1));
}

function formatDateKey(value: string): string {
  return new Intl.DateTimeFormat("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric"
  }).format(new Date(`${value}T12:00:00`));
}

function formatDoseCount(doseCount: number): string {
  if (doseCount === 0) {
    return "non preso";
  }
  return doseCount === 1 ? "1 dose" : `${doseCount} dosi`;
}

function formatEventCount(eventCount: number): string {
  return eventCount === 1 ? "1 evento" : `${eventCount} eventi`;
}

function formatGastricEventCount(eventCount: number): string {
  return eventCount === 1 ? "1 episodio gastrico" : `${eventCount} episodi gastrici`;
}

function formatSyncDate(value: string | null): string {
  if (!value) {
    return "Mai";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "Mai";
  }
  return new Intl.DateTimeFormat("it-IT", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23"
  }).format(date);
}

function syncErrorMessage(error: unknown): string {
  if (error instanceof SyncError) {
    return error.message;
  }
  return error instanceof Error ? error.message : "Errore locale durante la sincronizzazione.";
}

function isStandaloneMode(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    Boolean((navigator as Navigator & { standalone?: boolean }).standalone)
  );
}

function isIosDevice(): boolean {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

function pwaWorkerErrorMessage(worker: PwaWorkerState): string {
  if (worker.stage === "unsupported") {
    return "Questo browser non supporta l'uso offline della PWA.";
  }
  if (worker.error && /ssl|certificate|secure origin/i.test(worker.error)) {
    return "Il browser non considera attendibile il certificato HTTPS e ha bloccato l'uso offline.";
  }
  return worker.error
    ? `Preparazione offline non riuscita: ${worker.error}`
    : "Preparazione offline non riuscita.";
}

function caCertificateUrl(): string {
  return "/ca.crt";
}

function addDaysKey(dateKey: string, days: number): string {
  const date = new Date(`${dateKey}T12:00:00`);
  date.setDate(date.getDate() + days);
  return todayKey(date);
}

function monthKey(dateKey: string): string {
  return dateKey.slice(0, 7);
}

function formatHealthMonth(dateKey: string): string {
  return new Intl.DateTimeFormat("it-IT", { month: "short", year: "2-digit" })
    .format(new Date(`${dateKey}T12:00:00`))
    .replace(" ", " ");
}

function healthDayLabel(
  dateKey: string,
  day: ReturnType<typeof summarizeHealthDays>[number] | undefined
): string {
  const medication = day?.medication_recorded ? formatDoseCount(day.ipp_doses) : "nessun dato";
  const episode = day?.vomit_events ? formatGastricEventCount(day.vomit_events) : "nessun episodio gastrico";
  const symptoms = day
    ? SYMPTOM_KINDS.filter((kind) => day.symptoms[kind])
        .map((kind) => `${SYMPTOM_LABELS[kind]} ${day.symptoms[kind]}`)
        .join(", ")
    : "";
  const supportParts = day
    ? [
        day.doses_by_kind.antiacido > 0 ? `F.Tidina ${day.doses_by_kind.antiacido}` : "",
        day.doses_by_kind.brioschi > 0 ? `Brioschi ${day.doses_by_kind.brioschi}` : ""
      ].filter(Boolean)
    : [];
  const support = supportParts.length ? ` ${supportParts.join(", ")}.` : "";
  return `${formatLongDate(dateKey)}. IPP: ${medication}. ${episode}.${symptoms ? ` Sintomi: ${symptoms}.` : ""}${support}`;
}

function formatWeekRange(weekStart: string): string {
  const start = new Date(`${weekStart}T12:00:00`);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  const formatter = new Intl.DateTimeFormat("it-IT", { day: "numeric", month: "short" });
  return `${formatter.format(start)} – ${formatter.format(end)}`;
}

function formatLongDate(dateKey: string): string {
  return new Intl.DateTimeFormat("it-IT", {
    weekday: "long",
    day: "numeric",
    month: "long"
  }).format(new Date(`${dateKey}T12:00:00`));
}

function formatTime(iso: string): string {
  return new Intl.DateTimeFormat("it-IT", {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23"
  }).format(new Date(iso));
}

function formatWellbeingDateTime(iso: string): string {
  return new Intl.DateTimeFormat("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23"
  }).format(new Date(iso));
}

function mealTypeLabel(mealType: MealType): string {
  return mealTypeOptions.find((option) => option.value === mealType)?.label ?? "Pasto";
}

function mealTypeWithArticle(mealType: MealType): string {
  switch (mealType) {
    case "colazione":
      return "alla colazione";
    case "cena":
      return "alla cena";
    case "spuntino":
      return "allo spuntino";
    default:
      return "al pranzo";
  }
}

function mealTypeWithDefiniteArticle(mealType: MealType): string {
  switch (mealType) {
    case "colazione":
      return "la colazione";
    case "cena":
      return "la cena";
    case "spuntino":
      return "lo spuntino";
    default:
      return "il pranzo";
  }
}

function dateTimeFromInputs(dateValue: string, timeValue: string): string {
  const date = parseDateInput(dateValue);
  const time = parseClockInput(timeValue);
  if (!date) {
    throw new Error("Data non valida. Usa gg/mm/aaaa.");
  }
  if (!time) {
    throw new Error("Ora non valida. Usa ad esempio 730 o 18:50.");
  }
  const [year, month, day] = date.split("-").map(Number);
  return new Date(year, month - 1, day, time.hours, time.minutes, 0, 0).toISOString();
}

function transcriptionLabel(status: VoiceNote["transcription_status"]): string {
  switch (status) {
    case "queued":
      return "in coda";
    case "running":
      return "trascrizione in corso";
    case "done":
      return "trascritta";
    case "failed":
      return "trascrizione non riuscita";
    default:
      return "salvata in locale";
  }
}

function formatDurationEditorValue(minutes: number): string {
  if (minutes < 60) {
    return String(minutes);
  }
  return `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, "0")}`;
}

function formatRunning(startIso: string): string {
  const elapsed = Math.max(0, Date.now() - new Date(startIso).getTime());
  const hours = Math.floor(elapsed / 3_600_000);
  const minutes = Math.floor((elapsed % 3_600_000) / 60_000);
  const seconds = Math.floor((elapsed % 60_000) / 1_000);
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function preferredAudioMimeType(): string {
  return ["audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus", "audio/mp4"].find((type) =>
    MediaRecorder.isTypeSupported(type)
  ) ?? "";
}
